import { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Link,
  Card,
  CardContent,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  ButtonGroup,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import CircleIcon from "@mui/icons-material/Circle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TranslateIcon from "@mui/icons-material/Translate";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#1B7A3E",
      light: "#2EA055",
      dark: "#145C2F",
      contrastText: "#fff",
    },
    secondary: { main: "#4CAF50", light: "#81C784", dark: "#388E3C" },
    background: { default: "#F5FAF6", paper: "#FFFFFF" },
    success: { main: "#2E7D32", light: "#E8F5E9" },
    text: { primary: "#1A2E1C", secondary: "#4A5E4C" },
    divider: "#C8E6C9",
  },
  typography: {
    fontFamily: "'Georgia', 'Times New Roman', serif",
    h1: { fontFamily: "'Georgia', serif", fontWeight: 700 },
    h2: { fontFamily: "'Georgia', serif", fontWeight: 700 },
    h3: { fontFamily: "'Georgia', serif", fontWeight: 600 },
    h4: { fontFamily: "'Georgia', serif", fontWeight: 600 },
    h5: { fontFamily: "'Georgia', serif", fontWeight: 600 },
    h6: { fontFamily: "'Georgia', serif", fontWeight: 600 },
    body1: { fontFamily: "'Georgia', serif", lineHeight: 1.8 },
    body2: { fontFamily: "'Georgia', serif", lineHeight: 1.7 },
    subtitle1: { fontFamily: "'Georgia', serif" },
    subtitle2: { fontFamily: "'Georgia', serif", fontWeight: 600 },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: { boxShadow: "0 2px 16px rgba(27,122,62,0.07)" },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "#1B7A3E",
          color: "#fff",
          fontFamily: "'Georgia', serif",
          fontWeight: 600,
          fontSize: "0.92rem",
        },
        body: {
          fontFamily: "'Georgia', serif",
          fontSize: "0.9rem",
          color: "#1A2E1C",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: "'Georgia', serif",
          textTransform: "none",
          fontSize: "0.9rem",
          fontWeight: 500,
          color: "#4A5E4C",
          "&.Mui-selected": { color: "#1B7A3E", fontWeight: 700 },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: "1px solid #C8E6C9",
          borderRadius: "10px !important",
          marginBottom: "8px",
          "&:before": { display: "none" },
          "&.Mui-expanded": { boxShadow: "0 4px 16px rgba(27,122,62,0.10)" },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          "&.Mui-expanded": {
            backgroundColor: "#F0FAF2",
            borderBottom: "1px solid #C8E6C9",
          },
        },
      },
    },
  },
});

// ---- TRANSLATIONS ----
const translations = {
  en: {
    langLabel: "EN",
    langFull: "English",
    title: "TA Wallet",
    subtitle: "Transaction Analysts India Pvt. Ltd.",
    mainTitle: "Terms & Conditions",
    chips: [
      "RBI Licensed PPI",
      "Semi-Closed Wallet",
      "Payment & Settlement System Act 2007",
    ],
    tabs: [
      "Wallet Policy",
      "Product & Eligibility",
      "Loading & Spending",
      "Limits & Charges",
      "Privacy Policy",
      "Terms & Conditions",
      "Digital Services",
      "Bus Tickets",
      "User Environment",
    ],
    footer: {
      company: "Transaction Analysts India Pvt. Ltd.",
      email: "support@taipl.in",
      helpDesk: "Help Desk:",
    },
  },
  hi: {
    langLabel: "हिं",
    langFull: "हिंदी",
    title: "TA Wallet",
    subtitle: "Transaction Analysts India Pvt. Ltd.",
    mainTitle: "Terms & Conditions",
    chips: [
      "आरबीआई लाइसेंस प्राप्त पीपीआई",
      "अर्ध-बंद वॉलेट",
      "भुगतान और निपटान प्रणाली अधिनियम 2007",
    ],
    tabs: [
      "वॉलेट नीति",
      "उत्पाद और पात्रता",
      "लोडिंग और खर्च",
      "सीमाएं और शुल्क",
      "गोपनीयता नीति",
      "नियम और शर्तें",
      "डिजिटल सेवाएं",
      "बस टिकट",
      "उपयोगकर्ता परिवेश",
    ],
    footer: {
      company: "ट्रांजेक्शन एनालिस्ट इंडिया प्राइवेट लिमिटेड",
      email: "support@taipl.in",
      helpDesk: "हेल्प डेस्क:",
    },
  },
  kn: {
    langLabel: "ಕನ್ನ",
    langFull: "ಕನ್ನಡ",
    title: "TA Wallet",
    subtitle: "Transaction Analysts India Pvt. Ltd.",
    mainTitle: "Terms & Conditions",
    chips: [
      "ಆರ್ಬಿಐ ಪರವಾನಗಿ ಪಡೆದ ಪಿಪಿಐ",
      "ಅರೆ-ಮುಚ್ಚಿದ ವ್ಯಾಲೆಟ್",
      "ಪಾವತಿ ಮತ್ತು ಇತ್ಯರ್ಥ ವ್ಯವಸ್ಥೆ ಕಾಯ್ದೆ 2007",
    ],
    tabs: [
      "ವ್ಯಾಲೆಟ್ ನೀತಿ",
      "ಉತ್ಪನ್ನ ಮತ್ತು ಅರ್ಹತೆ",
      "ಲೋಡಿಂಗ್ ಮತ್ತು ಖರ್ಚು",
      "ಮಿತಿಗಳು ಮತ್ತು ಶುಲ್ಕಗಳು",
      "ಗೌಪ್ಯತಾ ನೀತಿ",
      "ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳು",
      "ಡಿಜಿಟಲ್ ಸೇವೆಗಳು",
      "ಬಸ್ ಟಿಕೆಟ್",
      "ಬಳಕೆದಾರ ಪರಿಸರ",
    ],
    footer: {
      company: "ಟ್ರಾನ್ಸಾಕ್ಷನ್ ಅನಾಲಿಸ್ಟ್ಸ್ ಇಂಡಿಯಾ ಪ್ರೈವೇಟ್ ಲಿಮಿಟೆಡ್",
      email: "support@taipl.in",
      helpDesk: "ಸಹಾಯ ಡೆಸ್ಕ್:",
    },
  },
};

function SectionTitle({ children }) {
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5, mt: 1 }}
    >
      <Box
        sx={{
          width: 4,
          height: 28,
          bgcolor: "primary.main",
          borderRadius: 2,
          flexShrink: 0,
        }}
      />
      <Typography
        variant="h5"
        sx={{ color: "primary.dark", fontWeight: 700, letterSpacing: 0.2 }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function SubTitle({ children }) {
  return (
    <Typography
      variant="h6"
      sx={{
        color: "#1B7A3E",
        fontWeight: 600,
        mt: 2.5,
        mb: 1.5,
        borderBottom: "1px solid #C8E6C9",
        pb: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

function NoteBox({ children, variant = "info" }) {
  const colors = {
    info: {
      bg: "#E8F5E9",
      border: "#4CAF50",
      icon: <InfoIcon sx={{ color: "#2E7D32", fontSize: 20 }} />,
    },
    warning: {
      bg: "#FFF8E1",
      border: "#FFC107",
      icon: <WarningAmberIcon sx={{ color: "#F57F17", fontSize: 20 }} />,
    },
  };
  const c = colors[variant];
  return (
    <Box
      sx={{
        bgcolor: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: 2,
        p: 2,
        my: 2,
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
      }}
    >
      {c.icon}
      <Typography variant="body2" sx={{ color: "#1A2E1C", lineHeight: 1.75 }}>
        {children}
      </Typography>
    </Box>
  );
}

function BulletList({ items }) {
  return (
    <List dense disablePadding>
      {items.map((item, i) => (
        <ListItem
          key={i}
          disableGutters
          sx={{ alignItems: "flex-start", py: 0.3 }}
        >
          <ListItemIcon sx={{ minWidth: 26, mt: "7px" }}>
            <CircleIcon sx={{ fontSize: 7, color: "primary.main" }} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                variant="body2"
                sx={{ color: "#1A2E1C", lineHeight: 1.75 }}
              >
                {item}
              </Typography>
            }
          />
        </ListItem>
      ))}
    </List>
  );
}

function StyledTable({ headers, rows, striped = true }) {
  return (
    <TableContainer
      component={Paper}
      sx={{
        my: 2,
        borderRadius: 2,
        border: "1px solid #C8E6C9",
        overflow: "hidden",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {headers.map((h, i) => (
              <TableCell key={i} sx={{ fontWeight: 700, py: 1.5, px: 2.5 }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow
              key={i}
              sx={{
                bgcolor: striped && i % 2 === 0 ? "#F5FAF6" : "#fff",
                "&:hover": { bgcolor: "#E8F5E9" },
                transition: "background 0.15s",
              }}
            >
              {row.map((cell, j) => (
                <TableCell
                  key={j}
                  sx={{ py: 1.5, px: 2.5, borderBottom: "1px solid #E8F5E9" }}
                >
                  {typeof cell === "string" ? (
                    <Typography variant="body2">{cell}</Typography>
                  ) : (
                    cell
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function AllowedChip({ allowed }) {
  return allowed ? (
    <Chip
      icon={<CheckCircleIcon />}
      label="Allowed"
      size="small"
      sx={{
        bgcolor: "#E8F5E9",
        color: "#1B7A3E",
        fontWeight: 600,
        "& .MuiChip-icon": { color: "#2E7D32" },
        fontFamily: "'Georgia', serif",
      }}
    />
  ) : (
    <Chip
      icon={<CancelIcon />}
      label="Not Allowed"
      size="small"
      sx={{
        bgcolor: "#FFEBEE",
        color: "#C62828",
        fontWeight: 600,
        "& .MuiChip-icon": { color: "#C62828" },
        fontFamily: "'Georgia', serif",
      }}
    />
  );
}

// ============================================================
// ENGLISH PANELS
// ============================================================
function WalletUsagePanelEN() {
  return (
    <Box>
      <SectionTitle>
        1. Wallet Usage Policy and Issue of Mobile Wallet
      </SectionTitle>
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: "primary.dark", fontSize: "1rem" }}
          >
            Wallets opened on or before 01-Mar-2018
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body2" gutterBottom>
            In terms of RBI Master Directions on PPI issued and updated (as on
            December 2017) all the existing (Refer Para 11) PPIs as on that day
            (28-February-2018) must be converted to Full KYC PPI as indicated in
            Para 9 (ii).
          </Typography>
          <Typography variant="body2" gutterBottom>
            In case if the PPI holder does not opt to convert wallet to Full
            KYC, then they must mandatory provide details as indicated in
            paragraph 9.1 (i). After providing the same you will be allowed to
            use the existing balance for purchase of goods and services.
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontStyle: "italic", my: 1, color: "#555" }}
          >
            (Or)
          </Typography>
          <Typography variant="body2" gutterBottom>
            You can also Register Bank account details (this can be done in the
            Profile Screen after Login) and seek closure and remit the balance
            amount to the Bank account. Such request may be placed to our e-Mail
            ID:{" "}
            <Link href="mailto:" sx={{ color: "primary.main" }}>
              support@taipl.in
            </Link>{" "}
            (along with the first page of the Bank pass book). After confirming
            the Bank account name with the wallet name we will remit the amount
            to the Bank account. No charges will be levied for such remittance.
            (Refer 11 (b), (c) and (d) of the Master directions).
          </Typography>
          <NoteBox variant="warning">
            <strong>NOTE:</strong> In case, if you do not convert your wallet
            into full KYC, further loading will not be allowed and you will be
            allowed only to usecare@transactionanalysts.com the outstanding
            balance.
          </NoteBox>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: "primary.dark", fontSize: "1rem" }}
          >
            Wallets opened AFTER 01-Mar-2018
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Minimum KYC wallet opened after the issue of the Circular
            (28-Feb-2018) must be converted into KYC compliant semi-closed PPIs
            (as defined in paragraph 9.1(ii)) within a period of 12 months from
            the date of issue of PPI, failing which no further credit shall be
            allowed in such PPIs.
          </Typography>
          <Typography variant="body2" gutterBottom>
            However, the PPI holder shall be allowed to use the balance
            available in the PPI and they can also Register their Bank account
            details (this can be done in the Profile Section after login) and
            seek closure and remit the balance amount to the Bank account. Such
            request may be placed to our e-Mail ID:{" "}
            <Link href="support@taipl.in" sx={{ color: "primary.main" }}>
              support@taipl.in
            </Link>{" "}
            (Along with the first page of the Bank pass book). After confirming
            the Bank account name with the wallet name we will remit the amount
            to the Bank account. No charges will be levied for such remittance.
            (Refer 11 (b), (c) and (d) of the Master directions).
          </Typography>
        </AccordionDetails>
      </Accordion>
      <NoteBox>
        <strong>As an RBI Licensed Semi closed PPI operator</strong> we are
        bound to FOLLOW the guidelines issued by RBI from time to time.
        Customers are requested to go through the latest RBI guidelines
        available at{" "}
        <Link
          href="https://transactionanalysts.com/"
          sx={{ color: "primary.main" }}
        >
          transactionanalysts.com
        </Link>{" "}
        and also at the RBI website under "Payment and settlement systems".
      </NoteBox>
      <NoteBox>
        For any Queries customers can always write to us at{" "}
        <Link href="mailto:support@taipl.in" sx={{ color: "primary.main" }}>
          support@taipl.in
        </Link>{" "}
        or call our help desk no. <strong>9916788339</strong>
      </NoteBox>
    </Box>
  );
}

function ProductPanelEN() {
  return (
    <Box>
      <SectionTitle>Product</SectionTitle>
      <Typography variant="body2" gutterBottom>
        Please read the following terms and conditions carefully before
        registering on, accessing, browsing, downloading or using the TA Wallet
        website (
        <Link href="https://www.tawallet.com" sx={{ color: "primary.main" }}>
          tawallet.com
        </Link>
        ) and all associated sites linked to TA Wallet, or the TA Wallet mobile
        application or any similar platform (hereinafter collectively referred
        to as, the "TA Wallet Platform") run by Transaction Analysts India Pvt.
        Ltd. who are licensed by RBI to issue Semi closed PPI Wallet under
        Payment and Settlement System Act 2007.
      </Typography>
      <Typography variant="body2" gutterBottom>
        A citizen can have only one TA Wallet which can be linked to his/her
        Mobile. The value in the Wallet can be used for money transfer between
        TA Wallet accounts, IMPS to Bank Account, make payments for the purchase
        of goods or services to any government department or merchant registered
        with the TA Wallet, and any other purposes which RBI allows given TA
        Wallet provide such services.
      </Typography>
      <SubTitle>Eligibility Criteria</SubTitle>
      <BulletList
        items={[
          "Any Citizen of India aged 18 Years and above (21 years if the guardian is appointed by the Court) is eligible for the Wallet.",
          "The citizen should not be a person who is disqualified by law to which he is subjected to.",
          "The citizen must be of sound mind when agreeing to the terms of use.",
          "An individual can have only one Wallet and it is the responsibility of the citizen to ensure this.",
          "In case if multiple wallets are found at any point of time, the balances in all the wallets will be clubbed for the purpose of Wallet a/c limits validation. Action will also be initiated as per the extant guidelines issued by RBI from time to time.",
        ]}
      />
      <SubTitle>Types of Wallet and General Conditions</SubTitle>
      <BulletList
        items={[
          "Presently TA is issuing TA Wallet with Full KYC and Low KYC. Both types of Wallet are Re-loadable in nature and issued for the usage within the Country.",
          "In the case of Low KYC wallets, it shall be used only for the purchase of goods and services. Funds transfer from such PPIs to bank accounts and also to PPIs of same/other issuers shall not be permitted.",
          "In the case of Full KYC both purchases of goods and services, and fund transfer (both internal and IMPS) facilities are available.",
          "Customer has the option to convert a Low-KYC wallet into Full-KYC wallet at any time.",
          "All wallets issued will have a validity period of one year from the date of issue.",
          "Further all wallets will have a validity period of one year from the date of last loading / reloading in the wallet.",
          "Wallets not operated for more than 12 months, after initial validity of one year from the date of issue, will be made INACTIVE. To keep the wallet active customer is requested to make at least one financial transaction (Debit or Credit) every month.",
          "A wallet account will be treated as EXPIRED upon expiration of 12 MONTHS after completion of ONE YEAR as INACTIVE account.",
          "Wallet holder can approach TA for refund of balance amount of EXPIRED wallet, at any time after the expiry date of wallet.",
          "Wallet holders are also requested to register their Bank account details, to which they want us to transfer the wallet balance at the time of closure of wallet. We have provided the facility of registering the Bank account details on our profile page.",
        ]}
      />
      <NoteBox variant="warning">
        <strong>Explanation:</strong> A wallet opened on 1.1.2025 will have a
        default validity of one year i.e., valid up to 1.1.2026 even though no
        operations are carried out by wallet holder during the period from
        1.1.2025 to 1.1.2026. Say, if last operation in the wallet took place on
        1.2.2026 and no further operations are carried out later, the wallet
        will be treated as INACTIVE on 1.2.2027. If wallet is not converted/made
        ACTIVE after 1.2.2027, then the wallet will be treated as EXPIRED on
        1.2.2028.
      </NoteBox>
      <NoteBox>
        *Note: All the above limits mentioned are Maximum Limits. However, based
        on the customer risk profile TA may fix lower limits.
      </NoteBox>
    </Box>
  );
}

function LoadingSpendingPanelEN() {
  return (
    <Box>
      <SectionTitle>Loading & Spending</SectionTitle>
      <SubTitle>Loading</SubTitle>
      <Typography variant="body2" gutterBottom sx={{ mb: 1.5 }}>
        PRESENTLY MONEY CAN BE LOADED TO THE WALLET IN FOLLOWING WAYS:
      </Typography>
      <BulletList
        items={[
          "By NEFT from any Bank account who provides NEFT facility.",
          "By IMPS from any Bank Account who provides IMPS facility.",
          "From Credit Card/Debit card/Net Banking. (Payment Gateway)",
          "By UPI from any Bank Account who provides UPI facility.",
        ]}
      />
      <SubTitle>Spending</SubTitle>
      <BulletList
        items={[
          "The TA Wallet can be used to make payments for Govt. Bills & Non-Govt. Bills of entities integrated with TA Wallet.",
          "At a group of clearly identified merchant locations/establishments which contract specifically with Transaction Analysts India Pvt. Ltd. (Issuer) to accept the TA Wallet.",
          "IF THE CUSTOMER OPTS for Rupay Prepaid card, then in Merchant locations where the Rupay Card is acceptable.",
          "TA Wallet does not permit cash withdrawal from the wallet.",
        ]}
      />
      <NoteBox variant="warning">
        *Note: Applicable charges for loading and any Merchant transaction will
        have to be borne by the customer.
      </NoteBox>
      <SubTitle>1. Low KYC Wallet Account Limits</SubTitle>
      <StyledTable
        headers={["Feature", "Status"]}
        rows={[
          [
            "Wallet Balance Limit",
            "Cannot exceed Rs.10,000/- at any given point of time",
          ],
          ["Funding Limit", "Rs. 10,000/- (Per Month)"],
          ["Recharges", <AllowedChip allowed={true} />],
          ["Bill Payments", <AllowedChip allowed={true} />],
          ["Ticket Booking (Bus/Flight/Hotel)", <AllowedChip allowed={true} />],
          [
            "Merchant Payments (QR/Barcode/OTP)",
            <AllowedChip allowed={true} />,
          ],
          ["Other Goods & Services", <AllowedChip allowed={true} />],
          ["Fund Transfer to other Wallets", <AllowedChip allowed={false} />],
          ["IMPS", <AllowedChip allowed={false} />],
          [
            "Account Conversion to Full KYC",
            "Option available at customer's request",
          ],
        ]}
      />
      <SubTitle>2. Full KYC Wallet Account Limits</SubTitle>
      <StyledTable
        headers={["Feature", "Status"]}
        rows={[
          [
            "Wallet Balance Limit",
            "Cannot exceed Rs.2,00,000/- at any given point of time",
          ],
          ["Recharges", <AllowedChip allowed={true} />],
          ["Bill Payments", <AllowedChip allowed={true} />],
          ["Ticket Booking (Bus/Flight/Hotel)", <AllowedChip allowed={true} />],
          [
            "Merchant Payments (QR/Barcode/OTP)",
            <AllowedChip allowed={true} />,
          ],
          ["Other Goods & Services", <AllowedChip allowed={true} />],
          ["Fund Transfer to other Wallets*", <AllowedChip allowed={true} />],
          ["IMPS*", <AllowedChip allowed={true} />],
        ]}
      />
      <NoteBox>
        *Note: All the above limits mentioned are Maximum Limits. However, based
        on the customer risk profile TA may fix lower limits.
      </NoteBox>
    </Box>
  );
}

function ChargesPanelEN() {
  return (
    <Box>
      <SectionTitle>3. Limits & Charges</SectionTitle>
      <SubTitle>Wallet Usage Charges</SubTitle>
      <Typography variant="body2" gutterBottom>
        The following are the charges and fees that are associated with the use
        of the instrument:
      </Typography>
      <StyledTable
        headers={["Usage Type", "Customer Charges"]}
        rows={[
          ["Issue of Wallet", "No Charges"],
          [
            "Converting Operative account into Inoperative account",
            "No Charges",
          ],
          [
            "Converting Inoperative account into Operative account",
            "No Charges",
          ],
          ["Closure of account", "No Charges"],
          ["Issue of virtual Rupay card", "No Charges"],
          [
            "Issue of Physical Rupay card including card delivery charges",
            "Rs.250/- plus GST",
          ],
          [
            "Issue of duplicate Physical Rupay card including card delivery charges",
            "Rs.250/- plus GST",
          ],
        ]}
      />
      <div id="load-money-charges">
        <SubTitle>Load Money Charges</SubTitle>

        <h4 style={{ marginTop: "20px", marginBottom: "10px" }}>PayU</h4>

        <StyledTable
          headers={["Transaction Type", "Customer Charges"]}
          rows={[
            ["Credit Card (CC)", "2.0% + GST"],
            ["Debit Card (Upto ₹2,000)", "0.40% + GST"],
            ["Debit Card (Greater than ₹2,000)", "0.85% + GST"],
            ["Net Banking - HDFC / ICICI", "1.6% + GST"],
            ["Net Banking - Other Banks", "1.1% + GST"],
          ]}
        />

        <h4 style={{ marginTop: "30px", marginBottom: "10px" }}>TAPAY</h4>

        <StyledTable
          headers={["Transaction Type", "Customer Charges"]}
          rows={[
            ["Credit Card (CC)", "1.94% + GST"],
            ["Debit Card (DC)", "0.95% + GST"],
          ]}
        />

        <NoteBox>
          The above charges will be debited to the customer's wallet online.
        </NoteBox>
      </div>
      <SubTitle>Fund Transfer Charges (Based on Amount)</SubTitle>

      <StyledTable
        headers={[
          "Amount Range",
          "Charges (₹)",
          "GST (₹)",
          "Total (₹)",
        ]}
        rows={[
          ["₹0.00 - ₹1,000.00", "4.24", "0.76", "5.00"],
          ["₹1,000.01 - ₹2,000.00", "8.47", "1.53", "10.00"],
          ["₹2,000.01 - ₹5,000.00", "12.71", "2.29", "15.00"],
          ["₹5,000.01 - ₹10,000.00", "25.42", "4.58", "30.00"],
          ["₹10,000.01 - ₹15,000.00", "63.56", "11.44", "75.00"],
          ["₹15,000.01 - ₹20,000.00", "84.75", "15.25", "100.00"],
          ["₹20,000.01 - ₹25,000.00", "127.12", "22.88", "150.00"],
          ["₹25,000.01 - ₹30,000.00", "152.54", "27.46", "180.00"],
          ["₹30,000.01 - ₹35,000.00", "177.97", "32.03", "210.00"],
          ["₹35,000.01 - ₹40,000.00", "203.39", "36.61", "240.00"],
          ["₹40,000.01 - ₹45,000.00", "228.81", "41.19", "270.00"],
          ["₹45,000.01 - ₹50,000.00", "254.24", "45.76", "300.00"],
        ]}
      />

      <SubTitle>Wallet Limits</SubTitle>

      <StyledTable
        headers={[
          "Limit Type",
          "Period",
          "Minimum KYC",
          "Full KYC",
        ]}
        rows={[
          [
            "Maximum Holding Balance",
            "At any time",
            "₹10,000",
            "₹2,00,000",
          ],
          [
            "Adding money to wallet(Includes receiving money from another user)",
            "Per Month",
            "₹10,000",
            "Unlimited",
          ],
          [
            "Adding money to wallet(Includes receiving money from another user)",
            "Per Year",
            "₹1,20,000",
            "Unlimited",
          ],
        ]}
      />

      {/* <NoteBox>
        The above fund transfer charges are inclusive of applicable GST and are
        calculated based on the transaction amount range.
      </NoteBox> */}
      <SubTitle>Load Money Limits</SubTitle>
      <Typography variant="body2" gutterBottom>
        Minimum amount per transaction: <strong>Rs. 10/-</strong>
      </Typography>
      <SubTitle>Cooling Periods</SubTitle>
      <StyledTable
        headers={[
          "Sl. No",
          "Nature of Activity",
          "Cooling Period for use of Funds",
        ]}
        rows={[
          ["1", "Loading (on opening)", "Immediate"],
          ["2", "Reloading", "Immediate"],
          ["3", "Addition of a new Beneficiary", "Immediate"],
          [
            "4",
            "Low KYC Registration",
            "Users can do payments after 4 hours from the time registration is successful.",
          ],
          [
            "5",
            "Full KYC Registration",
            "Users can do fund transfers (to another TA wallet and IMPS to Bank a/c) after 24 hours from the time registration is successful.",
          ],
        ]}
      />
      <NoteBox variant="warning">
        Note: The above limits (Load Money, Fund Transfer) may vary
        based on the directives from RBI. Any changes will be updated here.
      </NoteBox>
    </Box>
  );
}

function PrivacyPanelEN() {
  return (
    <Box>
      <SectionTitle>2. Privacy Policy</SectionTitle>
      <Typography variant="body2" gutterBottom>
        TA Wallet values your trust & respects your privacy. This Privacy Policy
        provides you with details about the manner in which your data is
        collected, stored & used by us. You are advised to read this Privacy
        Policy carefully. This policy is kept at{" "}
        <Link
          href="https://transactionanalysts.com/privacy-policy/"
          sx={{ color: "primary.main" }}
        >
          transactionanalysts.com/privacy-policy/
        </Link>
        . While you accept our Terms and Conditions and this Policy during your
        account opening or during any of the surveys conducted, you expressly
        give us consent to use & disclose your personal information in
        accordance with this Privacy Policy.
      </Typography>
      <NoteBox variant="warning">
        Our privacy policy may change at any time and any changes will be
        updated in our above website. So to make sure that you are aware of any
        changes, kindly visit our website periodically to review the policy.
      </NoteBox>
      <Typography variant="body2" gutterBottom>
        We will not sell, share or rent your personal information to any 3rd
        party or use your email address/mobile number for unsolicited emails
        and/or SMS. Any emails and/or SMS sent by us will only be in connection
        with the provision of agreed services & products and this Privacy
        Policy.
      </Typography>
      <Typography variant="body2" gutterBottom>
        Personal Information means and includes all information that can be
        linked to a specific individual or to identify any individual, such as
        name, address, mailing address, telephone number, email ID, credit card
        number, cardholder name, card expiration date, information about your
        mobile phone and any details that may have been voluntarily provided by
        the user in connection with availing any of the services on TA Wallet.
      </Typography>
      <Typography variant="body2" gutterBottom>
        We have stringent security measures in place to protect the loss,
        misuse, and alteration of the information under our control. Once you
        login to our application we offer the use of a secure server.
      </Typography>
    </Box>
  );
}

function TermsPanelEN() {
  const sections = [
    {
      title: "Terms & Conditions (General)",
      content: (
        <>
          <Typography variant="body2" gutterBottom>
            This website is designed, developed and maintained by Transaction
            Analysts India Pvt. Ltd. (hereinafter collectively referred to as,
            "TA").
          </Typography>
          <Typography variant="body2" gutterBottom>
            These terms and conditions shall be governed by and construed in
            accordance with the Indian Laws. Any dispute arising under these
            terms and conditions shall be subject to the jurisdiction of the
            courts of India.
          </Typography>
        </>
      ),
    },
    {
      title: "3. Consent",
      content: (
        <Typography variant="body2">
          By Accepting to our Terms and Conditions and Privacy Policy while
          opening the account, you consent to the collection and use of the
          information you disclose in accordance with this Privacy Policy.
        </Typography>
      ),
    },
    {
      title: "4. Acceptance",
      content: (
        <Typography variant="body2">
          By registering on, accessing, browsing, downloading or using the TA
          Wallet Platform for any general purpose or for the specific purpose of
          availing any TA Wallet Service, you agree to be bound by the
          single-User-id and password terms and conditions set forth below.
        </Typography>
      ),
    },
    {
      title: "5. Eligibility",
      content: (
        <Typography variant="body2">
          The TA Wallet Services are not available to persons under the age of
          18 or to anyone previously suspended or removed by TA Wallet from
          availing the TA Wallet Services or accessing the TA Wallet Platform.
        </Typography>
      ),
    },
    {
      title: "6. Other Terms and Conditions",
      content: (
        <Typography variant="body2">
          TA Wallet may make changes to any TA Wallet Services offered on the TA
          Wallet Platform, or to the applicable terms for any such TA Wallet
          Services, at any time, without notice.
        </Typography>
      ),
    },
    {
      title: "7. Communication Policy",
      content: (
        <Typography variant="body2">
          By accepting the T&Cs, you accept the following: TA Wallet may send
          alerts to the mobile phone number provided by you while registering
          with the TA Wallet Platform for the service or on any updated mobile
          number subsequently provided and recorded by us on the TA Wallet
          Platform, or via e-mail or push notifications.
        </Typography>
      ),
    },
    {
      title: "8. Use of TA Wallet Platform",
      content: (
        <Typography variant="body2">
          You understand that except for information, products or services
          clearly indicated as being supplied by TA Wallet, we do not operate,
          control, or endorse any information, products or services on the
          Internet in any way.
        </Typography>
      ),
    },
    {
      title: "9. Prohibited Conduct",
      content: (
        <>
          <Typography variant="body2" gutterBottom>
            By accessing or using the TA Wallet Platform or by availing TA
            Wallet Services, you agree not to violate the T&Cs, impersonate any
            person or entity, infringe intellectual property rights, use the TA
            Wallet Services if you are under the age of 18 years, post any
            offensive content, or engage in any fraudulent activity.
          </Typography>
        </>
      ),
    },
    {
      title: "10. Termination; Agreement Violations",
      content: (
        <Typography variant="body2">
          In no event, TA Wallet or its contractors, agents, licensors, partners
          or suppliers will be liable to you for any special, indirect,
          incidental, consequential, punitive, reliance, or exemplary damages.
        </Typography>
      ),
    },
    {
      title: "11. Limitation of Liability and Damages",
      content: (
        <Typography variant="body2">
          In no event, TA Wallet or its contractors, agents, licensors, partners
          or suppliers will be liable to you for any special, indirect,
          incidental, consequential damages beyond or in excess of Rs. 1,000,
          whichever is less.
        </Typography>
      ),
    },
    {
      title: "12. Indemnification",
      content: (
        <Typography variant="body2">
          You agree to indemnify, save, and hold TA Wallet, its affiliates,
          contractors, employees, officers, directors, agents and its
          third-party suppliers, licensors, and partners harmless from any and
          all claims, losses, damages, and liabilities.
        </Typography>
      ),
    },
    {
      title: "13. Disclaimer; No Warranties",
      content: (
        <Typography variant="body2">
          To the fullest extent permissible pursuant to applicable law, TA
          Wallet and its third-party partners, licensors and suppliers disclaim
          all warranties or guarantees — whether statutory, express or implied.
        </Typography>
      ),
    },
    {
      title: "14. Ownership; Proprietary Rights",
      content: (
        <Typography variant="body2">
          The TA Wallet Services and the TA Wallet Platform are owned and
          operated by TA Wallet and/or third-party licensors. All Materials,
          trademarks, service marks, and trade names are the property of TA
          Wallet.
        </Typography>
      ),
    },
    {
      title: "15. Modification of this Agreement",
      content: (
        <Typography variant="body2">
          TA Wallet reserves the right to change, modify, add, or remove
          portions of this Agreement at any time by posting the notification on
          the TA Wallet Platform or in our website.
        </Typography>
      ),
    },
    {
      title: "16. Notice",
      content: (
        <Typography variant="body2">
          TA Wallet may provide you with notices and communications by e-mail,
          SMS, push notifications, regular mail or postings on the TA Wallet
          Platform or by any other reasonable means.
        </Typography>
      ),
    },
    {
      title: "17. Waiver",
      content: (
        <Typography variant="body2">
          The failure of TA Wallet to exercise or enforce any right or provision
          of this Agreement will not constitute a waiver of such right or
          provision.
        </Typography>
      ),
    },
    {
      title: "18. Dispute Resolution",
      content: (
        <Typography variant="body2">
          If any dispute arises under this Agreement, the parties shall use all
          reasonable endeavors to resolve such Dispute amicably. The seat of the
          arbitration shall be Hyderabad and the language of this arbitration
          shall be English.
        </Typography>
      ),
    },
    {
      title: "19. Severability",
      content: (
        <Typography variant="body2">
          If any provision of this Agreement is held to be unlawful, void,
          invalid or otherwise unenforceable, then that provision will be
          limited or eliminated from this Agreement to the minimum extent
          required.
        </Typography>
      ),
    },
    {
      title: "20. Assignment",
      content: (
        <Typography variant="body2">
          This Agreement, and any rights granted here under, may not be
          transferred or assigned by you without TA Wallet's prior written
          consent.
        </Typography>
      ),
    },
    {
      title: "21. Survival",
      content: (
        <Typography variant="body2">
          Upon termination of this Agreement, any provision which, by its nature
          or express terms should survive, will survive such termination or
          expiration.
        </Typography>
      ),
    },
    {
      title: "22. Entire Agreement",
      content: (
        <Typography variant="body2">
          This Agreement is the entire agreement between you and TA Wallet
          relating to the subject matter hereof.
        </Typography>
      ),
    },
  ];
  return (
    <Box>
      <SectionTitle>Terms & Conditions</SectionTitle>
      {sections.map((s, i) => (
        <Accordion key={i}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "primary.dark", fontSize: "0.97rem" }}
            >
              {s.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1.5, pb: 2 }}>
            {s.content}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

function ServicesPanelEN() {
  const sections = [
    {
      title: "23. Bill Payments and Digital Products Terms & Conditions",
      content: (
        <Typography variant="body2">
          Please read the following terms and conditions carefully before
          registering on, accessing, browsing, downloading or using the TA
          Wallet Platform run by TA Wallet on any device and/or before availing
          any TA Wallet Services.
        </Typography>
      ),
    },
    {
      title: "24. Digital Services",
      content: (
        <Typography variant="body2">
          TA Wallet facilitates the payment of certain bills through the TA
          Wallet Platform with respect to certain services offered by TA Wallet
          business partners who have partnered with TA Wallet to enable their
          bills to be paid through the TA Wallet Platform.
        </Typography>
      ),
    },
    {
      title: "25. TA Wallet Recharges",
      content: (
        <Typography variant="body2">
          TA Wallet is only a facilitator of digital products. TA Wallet does
          not provide mobile operator services and is only a facilitator of
          prepaid mobile recharge services.
        </Typography>
      ),
    },
    {
      title: "26. Refund Policy",
      content: (
        <Typography variant="body2">
          All sales of prepaid recharge on the TA Wallet Platform are final with
          no refund or exchange permitted. If money has been charged to your
          card or bank account and a recharge is not delivered within 24 hours,
          then you shall inform us by sending an e mail to our customer services
          e mail address. You will be refunded the money within 21 working days
          from the date of receipt of your e mail.
        </Typography>
      ),
    },
    {
      title: "27. Bill Payments",
      content: (
        <Typography variant="body2">
          In order to use the bill payments service or any other TA Wallet
          Service, you will need to obtain access to the World Wide Web or the
          Internet. TA Wallet assumes no responsibility and shall incur no
          liability if it is unable to affect any payment instruction(s) on the
          payment date owing to circumstances beyond its control.
        </Typography>
      ),
    },
  ];
  return (
    <Box>
      <SectionTitle>Digital Services & Bill Payments</SectionTitle>
      {sections.map((s, i) => (
        <Accordion key={i}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "primary.dark", fontSize: "0.97rem" }}
            >
              {s.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1.5, pb: 2 }}>
            {s.content}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

function BusTicketsPanelEN() {
  return (
    <Box>
      <SectionTitle>28. Bus Tickets</SectionTitle>
      <Typography variant="body2" gutterBottom>
        TA Wallet is only a bus ticket agent. It does not operate buses or
        offers bus transport services. In order to provide a comprehensive
        choice of bus operators, departure times and prices to customers, TA
        Wallet has tied up with many bus operators and service providers.
      </Typography>
      <SubTitle>TA Wallet's Responsibilities Include:</SubTitle>
      <BulletList
        items={[
          "Issuing a valid ticket (a ticket that will be accepted by the bus operator) for its network of bus operators;",
          "Providing refund and support in the event of cancellation; and",
          "Providing customer support and information in case of any delays/inconvenience.",
        ]}
      />
      <SubTitle>TA Wallet's Responsibilities Do NOT Include:</SubTitle>
      <BulletList
        items={[
          "The bus operator's bus not departing/reaching on time;",
          "The bus operator's employees being unprofessional or engaging in unlawful conduct;",
          "The bus operator's bus seats, etc., not being up to the customer's expectation;",
          "The bus operator canceling the trip due to any reason;",
          "The baggage of the customer getting lost/stolen/damaged.",
        ]}
      />
      <NoteBox>
        The arrival and departure times mentioned on the ticket are only
        tentative timings. However, the bus will not leave the source before the
        time that is mentioned on the ticket.
      </NoteBox>
      <SubTitle>Boarding Requirements</SubTitle>
      <BulletList
        items={[
          "A copy of the ticket (a printout of the ticket or the printout of the ticket email).",
          "Identity proof (Aadhaar Card, Driving license, Student ID card, Company ID card, Passport, PAN card or Voter ID card). Failing to do so, they may not be allowed to board the bus.",
        ]}
      />
    </Box>
  );
}

function UserEnvPanelEN() {
  return (
    <Box>
      <SectionTitle>29. User Environment</SectionTitle>
      <SubTitle>Supported Browsers</SubTitle>
      <StyledTable
        headers={["Browser", "Minimum Version"]}
        rows={[
          ["Chrome", "Version 64 and Above"],
          ["Safari", "Version 12.1 and Above"],
          ["Microsoft Edge", "Version 69 and above"],
          ["Firefox", "Version 67 and above"],
        ]}
      />
      <SubTitle>Supported Mobile Platforms</SubTitle>
      <StyledTable
        headers={["Platform", "Minimum Version"]}
        rows={[
          ["Android (Smartphone)", "Version 11.0 and above"],
          ["iOS (iPhone)", "Version 13 and above"],
        ]}
      />
    </Box>
  );
}

// ============================================================
// HINDI PANELS
// ============================================================
function WalletUsagePanelHI() {
  return (
    <Box>
      <SectionTitle>1. वॉलेट उपयोग नीति और मोबाइल वॉलेट जारी करना</SectionTitle>
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: "primary.dark", fontSize: "1rem" }}
          >
            01-Mar-2018 को या उससे पहले खोले गए वॉलेट
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body2" gutterBottom>
            जारी एवं अद्यतन पीपीआई पर आरबीआई मास्टर निदेशों (दिसंबर 2017 को) के
            अनुसार, उस दिन (28 फरवरी, 2018 को) को सभी मौजूदा (पैरा 11 देखें)
            पीपीआई को पैरा 9 (ii) में दर्शाए अनुसार पूर्ण केवाईसी पीपीआई में
            परिवर्तित किया जाना चाहिए।
          </Typography>
          <Typography variant="body2" gutterBottom>
            यदि पीपीआई धारक वॉलेट को पूर्ण केवाईसी में बदलने का विकल्प नहीं
            चुनता है, तो उन्हें पैराग्राफ 9.1 (i) में बताए अनुसार विवरण प्रदान
            करना अनिवार्य होगा। ऐसा करने के बाद आपको वस्तुओं और सेवाओं की खरीद के
            लिए मौजूदा शेष राशि का उपयोग करने की अनुमति होगी।
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontStyle: "italic", my: 1, color: "#555" }}
          >
            (या)
          </Typography>
          <Typography variant="body2" gutterBottom>
            आप बैंक खाता विवरण भी पंजीकृत कर सकते हैं (यह लॉगिन के बाद प्रोफाइल
            स्क्रीन में किया जा सकता है) और बैंक खाते को बंद करने और शेष राशि
            भेजने की मांग कर सकते हैं। ऐसा अनुरोध हमारे ई-मेल आईडी पर किया जा
            सकता है:{" "}
            <Link href="mailto:support@taipl.in" sx={{ color: "primary.main" }}>
              support@taipl.in
            </Link>{" "}
            (बैंक पासबुक के पहले पृष्ठ के साथ)। वॉलेट के नाम के साथ बैंक खाते के
            नाम की पुष्टि करने के बाद हम राशि बैंक खाते में भेज देंगे। ऐसे
            प्रेषण के लिए कोई शुल्क नहीं लिया जाएगा। (मास्टर निदेशों के 11 (b),
            (c) और (d) देखें)।
          </Typography>
          <NoteBox variant="warning">
            <strong>नोट:</strong> यदि आप अपने वॉलेट को पूर्ण केवाईसी में नहीं
            बदलते हैं, तो आगे लोड करने की अनुमति नहीं दी जाएगी और आपको केवल
            बकाया राशि का उपयोग करने की अनुमति दी जाएगी।
          </NoteBox>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: "primary.dark", fontSize: "1rem" }}
          >
            01-Mar-2018 के बाद खोले गए वॉलेट
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body2" gutterBottom>
            परिपत्र जारी होने के बाद खोला गया न्यूनतम केवाईसी वॉलेट पीपीआई जारी
            करने की तारीख से 12 महीने की अवधि के भीतर केवाईसी अनुपालन अर्ध-बंद
            पीपीआई में परिवर्तित किया जाना चाहिए, ऐसा न करने पर ऐसे पीपीआई में
            आगे क्रेडिट की अनुमति नहीं दी जाएगी।
          </Typography>
          <Typography variant="body2" gutterBottom>
            हालांकि, पीपीआई धारक को पीपीआई में उपलब्ध शेष राशि का उपयोग करने की
            अनुमति होगी और वे अपने बैंक खाता विवरण भी पंजीकृत कर सकते हैं (यह
            लॉगिन के बाद प्रोफाइल सेक्शन में किया जा सकता है) और बैंक खाते को
            बंद करने और शेष राशि भेजने की मांग कर सकते हैं। ऐसा अनुरोध हमारे
            ई-मेल आईडी पर किया जा सकता है:{" "}
            <Link href="mailto:support@taipl.in" sx={{ color: "primary.main" }}>
              support@taipl.in
            </Link>{" "}
            (बैंक पासबुक के पहले पृष्ठ के साथ)। वॉलेट के नाम के साथ बैंक खाते के
            नाम की पुष्टि करने के बाद हम राशि बैंक खाते में भेज देंगे। ऐसे
            प्रेषण के लिए कोई शुल्क नहीं लिया जाएगा। (मास्टर निदेशों के 11 (b),
            (c) और (d) देखें)।
          </Typography>
        </AccordionDetails>
      </Accordion>
      <NoteBox>
        <strong>
          आरबीआई लाइसेंस प्राप्त अर्ध बंद पीपीआई ऑपरेटर के रूप में
        </strong>{" "}
        हम समय-समय पर आरबीआई द्वारा जारी दिशानिर्देशों का पालन करने के लिए बाध्य
        हैं। ग्राहकों से अनुरोध है कि वे{" "}
        <Link
          href="https://transactionanalysts.com/"
          sx={{ color: "primary.main" }}
        >
          transactionanalysts.com
        </Link>{" "}
        पर उपलब्ध नवीनतम आरबीआई दिशानिर्देशों को पढ़ें और साथ ही आरबीआई की
        वेबसाइट पर "भुगतान और निपटान प्रणाली" के अंतर्गत भी देखें।
      </NoteBox>
      <NoteBox>
        किसी भी प्रश्न के लिए ग्राहक हमेशा हमें{" "}
        <Link
          href="mailto:support@taipl.in"
          sx={{ color: "primary.main" }}
        >
          support@taipl.in
        </Link>{" "}
        पर लिख सकते हैं या हमारे हेल्प डेस्क नंबर पर कॉल कर सकते हैं।{" "}
        <strong>9916788339</strong>
      </NoteBox>
    </Box>
  );
}

function ProductPanelHI() {
  return (
    <Box>
      <SectionTitle>गुणनफल (उत्पाद)</SectionTitle>
      <Typography variant="body2" gutterBottom>
        ट्रांजेक्शन एनालिस्ट इंडिया प्राइवेट लिमिटेड द्वारा संचालित TA वॉलेट
        वेबसाइट (
        <Link href="https://www.tawallet.com" sx={{ color: "primary.main" }}>
          tawallet.com
        </Link>
        ), TA वॉलेट से जुड़ी सभी संबंधित साइटों, या TA वॉलेट मोबाइल एप्लिकेशन
        अथवा किसी भी समान प्लेटफॉर्म (जिसे इसके बाद सामूहिक रूप से "TA वॉलेट
        प्लेटफॉर्म" कहा जाएगा) पर पंजीकरण करने, एक्सेस करने, ब्राउज़ करने,
        डाउनलोड करने या उपयोग करने से पहले कृपया निम्नलिखित नियमों और शर्तों को
        ध्यान से पढ़ें। ट्रांजेक्शन एनालिस्ट इंडिया प्राइवेट लिमिटेड को भुगतान
        और निपटान प्रणाली अधिनियम 2007 के तहत अर्ध बंद पीपीआई वॉलेट जारी करने
        के लिए आरबीआई द्वारा लाइसेंस प्राप्त है।
      </Typography>
      <Typography variant="body2" gutterBottom>
        एक नागरिक के पास केवल एक टीए वॉलेट हो सकता है जिसे उसके मोबाइल से जोड़ा
        जा सकता है। वॉलेट में मूल्य का उपयोग टीए वॉलेट खातों के बीच धन
        हस्तांतरण, आईएमपीएस से बैंक खाते, टीए वॉलेट के साथ पंजीकृत किसी भी
        सरकारी विभाग या व्यापारी को वस्तुओं या सेवाओं की खरीद के लिए भुगतान करने,
        और किसी भी अन्य उद्देश्य के लिए किया जा सकता है जिसे आरबीआई अनुमति देता
        है, बशर्ते टीए वॉलेट ऐसी सेवाएं प्रदान करे।
      </Typography>
      <SubTitle>पात्रता मानदंड</SubTitle>
      <BulletList
        items={[
          "18 वर्ष और उससे अधिक आयु का भारत का कोई भी नागरिक (21 वर्ष यदि अभिभावक को न्यायालय द्वारा नियुक्त किया जाता है) वॉलेट के लिए पात्र है।",
          "नागरिक को ऐसा व्यक्ति नहीं होना चाहिए जो कानून द्वारा अयोग्य घोषित किया गया है।",
          "उपयोग की शर्तों से सहमत होने पर नागरिक को स्वस्थ दिमाग का होना चाहिए।",
          "एक व्यक्ति के पास केवल एक वॉलेट हो सकता है और यह सुनिश्चित करना नागरिक की जिम्मेदारी है।",
          "यदि किसी भी समय एक से अधिक वॉलेट पाए जाते हैं, तो सभी वॉलेट में शेष राशि को वॉलेट खाता सीमा सत्यापन के उद्देश्य से क्लब किया जाएगा। समय-समय पर आरबीआई द्वारा जारी वर्तमान दिशानिर्देशों के अनुसार कार्रवाई भी शुरू की जाएगी।",
        ]}
      />
      <SubTitle>वॉलेट के प्रकार और सामान्य स्थितियां</SubTitle>
      <BulletList
        items={[
          "वर्तमान में टीए पूर्ण केवाईसी और कम केवाईसी के साथ टीए वॉलेट जारी कर रहा है। दोनों प्रकार के वॉलेट प्रकृति में पुनः लोड करने योग्य हैं और देश के भीतर उपयोग के लिए जारी किए जाते हैं।",
          "कम केवाईसी वॉलेट के मामले में, इसका उपयोग केवल वस्तुओं और सेवाओं की खरीद के लिए किया जाएगा। ऐसे पीपीआई से बैंक खातों में और समान/अन्य जारीकर्ताओं के पीपीआई में फंड ट्रांसफर की अनुमति नहीं होगी।",
          "पूर्ण केवाईसी के मामले में वस्तुओं और सेवाओं की खरीद, और फंड ट्रांसफर (आंतरिक और आईएमपीएस दोनों) सुविधाएं उपलब्ध हैं।",
          "ग्राहक के पास किसी भी समय लो-केवाईसी वॉलेट को फुल-केवाईसी वॉलेट में बदलने का विकल्प है।",
          "जारी किए गए सभी वॉलेट की वैधता अवधि जारी होने की तारीख से एक वर्ष की होगी।",
          "इसके अलावा, सभी वॉलेट की वैधता अवधि वॉलेट में अंतिम लोडिंग/रीलोडिंग की तारीख से एक वर्ष की होगी।",
          "जारी होने की तारीख से एक वर्ष की प्रारंभिक वैधता के बाद, 12 महीने से अधिक समय तक संचालित नहीं होने वाले वॉलेट को निष्क्रिय कर दिया जाएगा। वॉलेट को सक्रिय रखने के लिए ग्राहक को हर महीने कम से कम एक वित्तीय लेनदेन (डेबिट या क्रेडिट) करने का अनुरोध किया जाता है।",
          "निष्क्रिय खाते के रूप में एक वर्ष पूरा होने के बाद 12 महीने की समाप्ति पर वॉलेट खाते को समाप्त माना जाएगा।",
          "वॉलेट धारक वॉलेट की समाप्ति तिथि के बाद किसी भी समय समाप्त वॉलेट की शेष राशि की वापसी के लिए TA से संपर्क कर सकते हैं।",
          "वॉलेट धारकों से यह भी अनुरोध किया जाता है कि वे अपने बैंक खाते का विवरण पंजीकृत करें, जिसमें वे वॉलेट बंद होने के समय वॉलेट शेष राशि स्थानांतरित करना चाहते हैं। हमने अपने प्रोफाइल पेज पर बैंक खाता विवरण पंजीकृत करने की सुविधा प्रदान की है।",
        ]}
      />
      <NoteBox variant="warning">
        <strong>स्पष्टीकरण:</strong> 1.1.2025 को खोले गए वॉलेट की एक वर्ष की
        डिफ़ॉल्ट वैधता होगी, अर्थात 1.1.2026 तक वैध होगी, भले ही 1.1.2025 से
        1.1.2026 की अवधि के दौरान वॉलेट धारक द्वारा कोई संचालन नहीं किया जाता है।
        मान लीजिए, यदि वॉलेट में अंतिम ऑपरेशन 1.2.2026 को हुआ था और बाद में कोई
        और संचालन नहीं किया जाता है, तो वॉलेट को 1.2.2027 को निष्क्रिय माना
        जाएगा। यदि वॉलेट को 1.2.2027 के बाद परिवर्तित/सक्रिय नहीं किया जाता है,
        तो वॉलेट को 1.2.2028 को समाप्त माना जाएगा।
      </NoteBox>
      <NoteBox>
        *नोट: उपरोक्त सभी उल्लिखित सीमाएं अधिकतम सीमाएं हैं। हालांकि, ग्राहक के
        जोखिम प्रोफाइल के आधार पर TA कम सीमाएं तय कर सकता है।
      </NoteBox>
    </Box>
  );
}

function LoadingSpendingPanelHI() {
  return (
    <Box>
      <SectionTitle>लोडिंग और खर्च</SectionTitle>
      <SubTitle>लोड</SubTitle>
      <Typography variant="body2" gutterBottom sx={{ mb: 1.5 }}>
        वर्तमान में वॉलेट में निम्नलिखित तरीकों से पैसा लोड किया जा सकता है:
      </Typography>
      <BulletList
        items={[
          "एनईएफटी सुविधा प्रदान करने वाले किसी भी बैंक खाते से एनईएफटी द्वारा।",
          "आईएमपीएस द्वारा किसी भी बैंक खाते से जो आईएमपीएस सुविधा प्रदान करता है।",
          "क्रेडिट कार्ड/डेबिट कार्ड/नेट बैंकिंग से। (पेमेंट गेटवे)",
          "यूपीआई सुविधा प्रदान करने वाले किसी भी बैंक खाते से यूपीआई द्वारा।",
        ]}
      />
      <SubTitle>खर्च</SubTitle>
      <BulletList
        items={[
          "TA वॉलेट का उपयोग सरकारी बिलों और गैर-सरकारी बिलों के भुगतान के लिए किया जा सकता है।",
          "स्पष्ट रूप से पहचाने गए व्यापारी स्थानों/प्रतिष्ठानों में जो विशेष रूप से लेनदेन विश्लेषक इंडिया प्राइवेट लिमिटेड के साथ अनुबंधित हैं।",
          "यदि ग्राहक रुपे प्रीपेड कार्ड का विकल्प चुनता है, तो जहां रुपे कार्ड स्वीकार्य है।",
          "TA वॉलेट वॉलेट से नकद निकासी की अनुमति नहीं देता।",
        ]}
      />
      <NoteBox variant="warning">
        *नोट: लोडिंग और किसी भी मर्चेंट लेनदेन के लिए लागू शुल्क ग्राहक को वहन
        करना होगा।
      </NoteBox>
      <SubTitle>1. कम केवाईसी वॉलेट खाता सीमाएं</SubTitle>
      <StyledTable
        headers={["सुविधा", "स्थिति"]}
        rows={[
          ["वॉलेट बैलेंस सीमा", "किसी भी समय Rs.10,000/- से अधिक नहीं हो सकती"],
          ["फंडिंग सीमा", "Rs. 10,000/- (प्रति माह)"],
          ["रिचार्ज", <AllowedChip allowed={true} />],
          ["बिल भुगतान", <AllowedChip allowed={true} />],
          ["टिकट बुकिंग (बस/उड़ान/होटल)", <AllowedChip allowed={true} />],
          ["मर्चेंट भुगतान (QR/बारकोड/OTP)", <AllowedChip allowed={true} />],
          ["अन्य वस्तुएं और सेवाएं", <AllowedChip allowed={true} />],
          ["अन्य वॉलेट में फंड ट्रांसफर", <AllowedChip allowed={false} />],
          ["आईएमपीएस", <AllowedChip allowed={false} />],
          [
            "पूर्ण केवाईसी में खाता रूपांतरण",
            "ग्राहक के अनुरोध पर विकल्प उपलब्ध है",
          ],
        ]}
      />
      <SubTitle>2. पूर्ण केवाईसी वॉलेट खाता सीमाएं</SubTitle>
      <StyledTable
        headers={["सुविधा", "स्थिति"]}
        rows={[
          [
            "वॉलेट बैलेंस सीमा",
            "किसी भी समय Rs.2,00,000/- से अधिक नहीं हो सकती",
          ],
          ["रिचार्ज", <AllowedChip allowed={true} />],
          ["बिल भुगतान", <AllowedChip allowed={true} />],
          ["टिकट बुकिंग (बस/उड़ान/होटल)", <AllowedChip allowed={true} />],
          ["मर्चेंट भुगतान (QR/बारकोड/OTP)", <AllowedChip allowed={true} />],
          ["अन्य वस्तुएं और सेवाएं", <AllowedChip allowed={true} />],
          ["अन्य वॉलेट में फंड ट्रांसफर*", <AllowedChip allowed={true} />],
          ["आईएमपीएस*", <AllowedChip allowed={true} />],
        ]}
      />
      <NoteBox>
        *नोट: उपरोक्त सभी उल्लिखित सीमाएं अधिकतम सीमाएं हैं। हालांकि, ग्राहक के
        जोखिम प्रोफाइल के आधार पर TA कम सीमाएं तय कर सकता है।
      </NoteBox>
    </Box>
  );
}

function ChargesPanelHI() {
  return (
    <Box>
      <SectionTitle>3. सीमाएं और शुल्क</SectionTitle>
      <SubTitle>वॉलेट उपयोग शुल्क</SubTitle>
      <Typography variant="body2" gutterBottom>
        इस साधन के उपयोग से जुड़े शुल्क और फीस निम्नलिखित हैं:
      </Typography>
      <StyledTable
        headers={["उपयोग प्रकार", "ग्राहक शुल्क"]}
        rows={[
          ["वॉलेट जारी करना", "कोई शुल्क नहीं"],
          ["ऑपरेटिव खाते को निष्क्रिय में बदलना", "कोई शुल्क नहीं"],
          ["निष्क्रिय खाते को ऑपरेटिव में बदलना", "कोई शुल्क नहीं"],
          ["खाता बंद करना", "कोई शुल्क नहीं"],
          ["वर्चुअल रुपे कार्ड जारी करना", "कोई शुल्क नहीं"],
          [
            "कार्ड डिलीवरी शुल्क सहित भौतिक रुपे कार्ड जारी करना",
            "Rs.250/- + GST",
          ],
          [
            "कार्ड डिलीवरी शुल्क सहित डुप्लिकेट भौतिक रुपे कार्ड जारी करना",
            "Rs.250/- + GST",
          ],
        ]}
      />
      <div id="load-money-charges">
        <SubTitle>लोड मनी चार्ज</SubTitle>

        <h4 style={{ marginTop: "20px", marginBottom: "10px" }}>PayU</h4>

        <StyledTable
          headers={["लेनदेन प्रकार", "ग्राहक शुल्क"]}
          rows={[
            ["क्रेडिट कार्ड (CC)", "2.0% + GST"],
            ["डेबिट कार्ड (₹2,000 तक)", "0.40% + GST"],
            ["डेबिट कार्ड (₹2,000 से अधिक)", "0.85% + GST"],
            ["नेट बैंकिंग - HDFC / ICICI", "1.6% + GST"],
            ["नेट बैंकिंग - अन्य बैंक", "1.1% + GST"],
          ]}
        />

        <h4 style={{ marginTop: "30px", marginBottom: "10px" }}>TAPAY</h4>

        <StyledTable
          headers={["लेनदेन प्रकार", "ग्राहक शुल्क"]}
          rows={[
            ["क्रेडिट कार्ड (CC)", "1.94% + GST"],
            ["डेबिट कार्ड (DC)", "0.95% + GST"],
          ]}
        />

        <NoteBox>
          उपरोक्त शुल्क ग्राहक के वॉलेट से ऑनलाइन डेबिट किए जाएंगे।
        </NoteBox>
      </div>
      <SubTitle>फंड ट्रांसफर शुल्क (राशि के आधार पर)</SubTitle>

      <StyledTable
        headers={["राशि सीमा", "शुल्क (₹)", "GST (₹)", "कुल (₹)"]}
        rows={[
          ["₹0.00 - ₹1,000.00", "4.24", "0.76", "5.00"],
          ["₹1,000.01 - ₹2,000.00", "8.47", "1.53", "10.00"],
          ["₹2,000.01 - ₹5,000.00", "12.71", "2.29", "15.00"],
          ["₹5,000.01 - ₹10,000.00", "25.42", "4.58", "30.00"],
          ["₹10,000.01 - ₹15,000.00", "63.56", "11.44", "75.00"],
          ["₹15,000.01 - ₹20,000.00", "84.75", "15.25", "100.00"],
          ["₹20,000.01 - ₹25,000.00", "127.12", "22.88", "150.00"],
          ["₹25,000.01 - ₹30,000.00", "152.54", "27.46", "180.00"],
          ["₹30,000.01 - ₹35,000.00", "177.97", "32.03", "210.00"],
          ["₹35,000.01 - ₹40,000.00", "203.39", "36.61", "240.00"],
          ["₹40,000.01 - ₹45,000.00", "228.81", "41.19", "270.00"],
          ["₹45,000.01 - ₹50,000.00", "254.24", "45.76", "300.00"],
        ]}
      />

      <SubTitle>वॉलेट सीमाएं</SubTitle>

      <StyledTable
        headers={["सीमा प्रकार", "अवधि", "न्यूनतम केवाईसी", "पूर्ण केवाईसी"]}
        rows={[
          ["अधिकतम धारण शेष राशि", "किसी भी समय", "₹10,000", "₹2,00,000"],
          [
            "वॉलेट में पैसा जोड़ना (अन्य उपयोगकर्ता से पैसा प्राप्त करना सहित)",
            "प्रति माह",
            "₹10,000",
            "असीमित",
          ],
          [
            "वॉलेट में पैसा जोड़ना (अन्य उपयोगकर्ता से पैसा प्राप्त करना सहित)",
            "प्रति वर्ष",
            "₹1,20,000",
            "असीमित",
          ],
        ]}
      />

      <SubTitle>लोड मनी सीमाएं</SubTitle>
      <Typography variant="body2" gutterBottom>
        प्रति लेनदेन न्यूनतम राशि: <strong>Rs. 10/-</strong>
      </Typography>
      <SubTitle>कूलिंग पीरियड</SubTitle>
      <StyledTable
        headers={[
          "क्र.सं.",
          "गतिविधि का प्रकार",
          "धन उपयोग के लिए कूलिंग पीरियड",
        ]}
        rows={[
          ["1", "लोडिंग (खोलने पर)", "तत्काल"],
          ["2", "रीलोडिंग", "तत्काल"],
          ["3", "नए लाभार्थी का जोड़ना", "तत्काल"],
          ["4", "कम केवाईसी पंजीकरण", "पंजीकरण सफल होने के 4 घंटे बाद।"],
          [
            "5",
            "पूर्ण केवाईसी पंजीकरण",
            "पंजीकरण सफल होने के 24 घंटे बाद फंड ट्रांसफर।",
          ],
        ]}
      />
      <NoteBox variant="warning">
        नोट: उपरोक्त सीमाएं (लोड मनी, फंड ट्रांसफर) आरबीआई के निर्देशों के आधार
        पर भिन्न हो सकती हैं। किसी भी बदलाव को यहां अपडेट किया जाएगा।
      </NoteBox>
    </Box>
  );
}

function PrivacyPanelHI() {
  return (
    <Box>
      <SectionTitle>2. गोपनीयता नीति</SectionTitle>
      <Typography variant="body2" gutterBottom>
        TA वॉलेट आपके विश्वास को महत्व देता है और आपकी गोपनीयता का सम्मान करता
        है। यह गोपनीयता नीति आपको उस तरीके के बारे में विवरण प्रदान करती है
        जिसमें आपका डेटा हमारे द्वारा एकत्र, संग्रहीत और उपयोग किया जाता है।
        आपको सलाह दी जाती है कि इस गोपनीयता नीति को ध्यान से पढ़ें। यह नीति{" "}
        <Link
          href="https://transactionanalysts.com/privacy-policy/"
          sx={{ color: "primary.main" }}
        >
          transactionanalysts.com/privacy-policy/
        </Link>{" "}
        पर रखी गई है। खाता खोलते समय या किसी भी सर्वेक्षण के दौरान हमारी नियम
        और शर्तों और इस नीति को स्वीकार करके, आप इस गोपनीयता नीति के अनुसार
        अपनी व्यक्तिगत जानकारी का उपयोग और प्रकटीकरण करने के लिए स्पष्ट रूप से
        सहमति देते हैं।
      </Typography>
      <NoteBox variant="warning">
        हमारी गोपनीयता नीति किसी भी समय बदल सकती है और किसी भी बदलाव को हमारी
        उपरोक्त वेबसाइट पर अपडेट किया जाएगा। इसलिए यह सुनिश्चित करने के लिए कि
        आप किसी भी बदलाव से अवगत हैं, कृपया समय-समय पर नीति की समीक्षा करने के
        लिए हमारी वेबसाइट पर जाएं।
      </NoteBox>
      <Typography variant="body2" gutterBottom>
        हम आपकी व्यक्तिगत जानकारी को किसी तीसरे पक्ष को नहीं बेचेंगे, साझा नहीं
        करेंगे या किराए पर नहीं देंगे या अनचाहे ईमेल और/या एसएमएस के लिए आपके
        ईमेल पते/मोबाइल नंबर का उपयोग नहीं करेंगे। हमारे द्वारा भेजे गए किसी भी
        ईमेल और/या एसएमएस केवल सहमत सेवाओं और उत्पादों के प्रावधान तथा इस
        गोपनीयता नीति के संबंध में होंगे।
      </Typography>
      <Typography variant="body2" gutterBottom>
        व्यक्तिगत जानकारी का अर्थ है और इसमें वह सभी जानकारी शामिल है जो किसी
        विशिष्ट व्यक्ति से जुड़ी हो सकती है या किसी व्यक्ति की पहचान कर सकती है,
        जैसे नाम, पता, मेलिंग पता, टेलीफोन नंबर, ईमेल आईडी, क्रेडिट कार्ड नंबर,
        कार्डधारक का नाम, कार्ड समाप्ति तिथि, आपके मोबाइल फोन के बारे में
        जानकारी और कोई भी विवरण जो उपयोगकर्ता द्वारा TA वॉलेट पर किसी भी सेवा
        का लाभ उठाने के संबंध में स्वेच्छा से प्रदान किया गया हो।
      </Typography>
      <Typography variant="body2" gutterBottom>
        हमारे नियंत्रण में जानकारी की हानि, दुरुपयोग और परिवर्तन से बचाने के लिए
        हमारे पास कड़े सुरक्षा उपाय हैं। एक बार जब आप हमारे एप्लिकेशन में लॉगिन
        करते हैं, तो हम एक सुरक्षित सर्वर के उपयोग की पेशकश करते हैं।
      </Typography>
    </Box>
  );
}

function TermsPanelHI() {
  const sections = [
    {
      title: "नियम और शर्तें (सामान्य)",
      content: (
        <>
          <Typography variant="body2" gutterBottom>
            यह वेबसाइट ट्रांजेक्शन एनालिस्ट इंडिया प्राइवेट लिमिटेड (इसके बाद
            सामूहिक रूप से "TA" कहा जाएगा) द्वारा डिजाइन, विकसित और अनुरक्षित
            है।
          </Typography>
          <Typography variant="body2" gutterBottom>
            इन नियमों और शर्तों को भारतीय कानूनों के अनुसार नियंत्रित और समझा
            जाएगा। इन नियमों और शर्तों के तहत उत्पन्न होने वाला कोई भी विवाद
            भारत की अदालतों के अधिकार क्षेत्र के अधीन होगा।
          </Typography>
        </>
      ),
    },
    {
      title: "3. सहमति",
      content: (
        <Typography variant="body2">
          खाता खोलते समय हमारी नियम और शर्तों और गोपनीयता नीति को स्वीकार करके,
          आप इस गोपनीयता नीति के अनुसार आपके द्वारा प्रकट की गई जानकारी के
          संग्रह और उपयोग के लिए सहमति देते हैं।
        </Typography>
      ),
    },
    {
      title: "4. स्वीकृति",
      content: (
        <Typography variant="body2">
          TA वॉलेट प्लेटफॉर्म पर पंजीकरण करके, एक्सेस करके, ब्राउज़ करके,
          डाउनलोड करके या किसी भी सामान्य उद्देश्य के लिए या किसी विशिष्ट TA
          वॉलेट सेवा का लाभ उठाने के लिए उपयोग करके, आप नीचे दिए गए एकल
          उपयोगकर्ता आईडी और पासवर्ड नियमों और शर्तों से बाध्य होने के लिए सहमत
          होते हैं।
        </Typography>
      ),
    },
    {
      title: "5. पात्रता",
      content: (
        <Typography variant="body2">
          TA वॉलेट सेवाएं 18 वर्ष से कम आयु के व्यक्तियों या TA वॉलेट द्वारा
          पहले निलंबित या हटाए गए किसी भी व्यक्ति के लिए उपलब्ध नहीं हैं जो TA
          वॉलेट सेवाओं का लाभ उठाने या TA वॉलेट प्लेटफॉर्म तक पहुंचने से रोका
          गया हो।
        </Typography>
      ),
    },
    {
      title: "6. अन्य नियम और शर्तें",
      content: (
        <Typography variant="body2">
          TA वॉलेट बिना किसी सूचना के किसी भी समय TA वॉलेट प्लेटफॉर्म पर दी जाने
          वाली किसी भी TA वॉलेट सेवाओं में, या ऐसी किसी भी TA वॉलेट सेवाओं पर
          लागू शर्तों में बदलाव कर सकता है।
        </Typography>
      ),
    },
    {
      title: "7. संचार नीति",
      content: (
        <Typography variant="body2">
          T&C को स्वीकार करके, आप निम्नलिखित को स्वीकार करते हैं: TA वॉलेट आपके
          द्वारा सेवा के लिए TA वॉलेट प्लेटफॉर्म के साथ पंजीकरण करते समय दिए गए
          मोबाइल फोन नंबर पर, या बाद में हमारे द्वारा TA वॉलेट प्लेटफॉर्म पर
          दर्ज किए गए किसी भी अद्यतन मोबाइल नंबर पर, या ईमेल या पुश नोटिफिकेशन
          के माध्यम से अलर्ट भेज सकता है।
        </Typography>
      ),
    },
    {
      title: "8. TA वॉलेट प्लेटफॉर्म का उपयोग",
      content: (
        <Typography variant="body2">
          आप समझते हैं कि TA वॉलेट द्वारा स्पष्ट रूप से आपूर्ति की गई जानकारी,
          उत्पादों या सेवाओं को छोड़कर, हम इंटरनेट पर किसी भी जानकारी, उत्पादों
          या सेवाओं का संचालन, नियंत्रण या समर्थन किसी भी तरह से नहीं करते हैं।
        </Typography>
      ),
    },
    {
      title: "9. निषिद्ध आचरण",
      content: (
        <Typography variant="body2" gutterBottom>
          TA वॉलेट प्लेटफॉर्म का उपयोग करके या TA वॉलेट सेवाओं का लाभ उठाकर,
          आप T&C का उल्लंघन नहीं करने, किसी भी व्यक्ति या संस्था का प्रतिरूपण
          नहीं करने, बौद्धिक संपदा अधिकारों का उल्लंघन नहीं करने, यदि आप 18
          वर्ष से कम आयु के हैं तो TA वॉलेट सेवाओं का उपयोग नहीं करने, कोई भी
          आपत्तिजनक सामग्री पोस्ट नहीं करने, या किसी भी धोखाधड़ी गतिविधि में
          शामिल नहीं होने के लिए सहमत होते हैं।
        </Typography>
      ),
    },
    {
      title: "10. समाप्ति; समझौते का उल्लंघन",
      content: (
        <Typography variant="body2">
          यदि आप इन नियमों और शर्तों का उल्लंघन करते हैं, तो TA वॉलेट किसी भी
          समय, बिना किसी सूचना के, अपने विवेकाधिकार से आपकी सेवाओं तक पहुंच को
          निलंबित या समाप्त कर सकता है।
        </Typography>
      ),
    },
    {
      title: "11. दायित्व और हर्जाने की सीमा",
      content: (
        <Typography variant="body2">
          किसी भी स्थिति में, TA वॉलेट या उसके ठेकेदार, एजेंट, लाइसेंसकर्ता,
          भागीदार या आपूर्तिकर्ता आपके प्रति किसी विशेष, अप्रत्यक्ष, आकस्मिक,
          परिणामी क्षति के लिए उत्तरदायी नहीं होंगे, जो रु. 1,000 से अधिक न हो,
          जो भी कम हो।
        </Typography>
      ),
    },
    {
      title: "12. क्षतिपूर्ति",
      content: (
        <Typography variant="body2">
          आप TA वॉलेट, उसकी सहयोगी संस्थाओं, ठेकेदारों, कर्मचारियों,
          अधिकारियों, निदेशकों, एजेंटों और उसके तृतीय-पक्ष आपूर्तिकर्ताओं,
          लाइसेंसकर्ताओं और भागीदारों को किसी भी और सभी दावों, हानियों,
          नुकसानों और देनदारियों से क्षतिपूर्ति देने, बचाने और सुरक्षित रखने के
          लिए सहमत हैं।
        </Typography>
      ),
    },
    {
      title: "13. अस्वीकरण; कोई वारंटी नहीं",
      content: (
        <Typography variant="body2">
          लागू कानून द्वारा अनुमत पूरी सीमा तक, TA वॉलेट और उसके तृतीय-पक्ष
          भागीदार, लाइसेंसकर्ता और आपूर्तिकर्ता सभी वारंटी या गारंटी को
          अस्वीकार करते हैं - चाहे वह वैधानिक, स्पष्ट या निहित हो।
        </Typography>
      ),
    },
    {
      title: "14. स्वामित्व; स्वामित्व अधिकार",
      content: (
        <Typography variant="body2">
          TA वॉलेट सेवाएं और TA वॉलेट प्लेटफॉर्म TA वॉलेट और/या तृतीय-पक्ष
          लाइसेंसकर्ताओं के स्वामित्व और संचालन में हैं। सभी सामग्री, ट्रेडमार्क,
          सेवा चिह्न और व्यापार नाम TA वॉलेट की संपत्ति हैं।
        </Typography>
      ),
    },
    {
      title: "15. इस समझौते में संशोधन",
      content: (
        <Typography variant="body2">
          TA वॉलेट किसी भी समय TA वॉलेट प्लेटफॉर्म पर या हमारी वेबसाइट पर
          सूचना पोस्ट करके इस समझौते के किसी भी हिस्से को बदलने, संशोधित करने,
          जोड़ने या हटाने का अधिकार सुरक्षित रखता है।
        </Typography>
      ),
    },
    {
      title: "16. सूचना",
      content: (
        <Typography variant="body2">
          TA वॉलेट आपको ई-मेल, एसएमएस, पुश नोटिफिकेशन, नियमित मेल या TA वॉलेट
          प्लेटफॉर्म पर पोस्टिंग द्वारा या किसी अन्य उचित माध्यम से सूचना और
          संचार प्रदान कर सकता है।
        </Typography>
      ),
    },
    {
      title: "17. अधित्याग",
      content: (
        <Typography variant="body2">
          इस समझौते के किसी भी अधिकार या प्रावधान को लागू करने में TA वॉलेट की
          विफलता ऐसे अधिकार या प्रावधान के अधित्याग का गठन नहीं करेगी।
        </Typography>
      ),
    },
    {
      title: "18. विवाद समाधान",
      content: (
        <Typography variant="body2">
          यदि इस समझौते के तहत कोई विवाद उत्पन्न होता है, तो पक्ष ऐसे विवाद को
          सौहार्दपूर्ण ढंग से हल करने के लिए सभी उचित प्रयास करेंगे। मध्यस्थता
          का स्थान हैदराबाद होगा और भाषा अंग्रेजी होगी।
        </Typography>
      ),
    },
    {
      title: "19. वियोज्यता",
      content: (
        <Typography variant="body2">
          यदि इस समझौते का कोई प्रावधान गैरकानूनी, शून्य, अमान्य या अन्यथा
          अप्रवर्तनीय पाया जाता है, तो उस प्रावधान को आवश्यक न्यूनतम सीमा तक
          सीमित या इस समझौते से हटा दिया जाएगा।
        </Typography>
      ),
    },
    {
      title: "20. समनुदेशन",
      content: (
        <Typography variant="body2">
          यह समझौता, और इसके तहत दिए गए कोई भी अधिकार, TA वॉलेट की पूर्व
          लिखित सहमति के बिना आपके द्वारा हस्तांतरित या समनुदेशित नहीं किए जा
          सकते।
        </Typography>
      ),
    },
    {
      title: "21. उत्तरजीविता",
      content: (
        <Typography variant="body2">
          इस समझौते की समाप्ति पर, कोई भी प्रावधान जो अपनी प्रकृति या स्पष्ट
          शर्तों के अनुसार जीवित रहना चाहिए, ऐसी समाप्ति या समाप्ति के बाद भी
          जीवित रहेगा।
        </Typography>
      ),
    },
    {
      title: "22. संपूर्ण समझौता",
      content: (
        <Typography variant="body2">
          यह समझौता इसके विषय के संबंध में आपके और TA वॉलेट के बीच संपूर्ण
          समझौता है।
        </Typography>
      ),
    },
  ];
  return (
    <Box>
      <SectionTitle>नियम और शर्तें</SectionTitle>
      {sections.map((s, i) => (
        <Accordion key={i}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "primary.dark", fontSize: "0.97rem" }}
            >
              {s.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1.5, pb: 2 }}>
            {s.content}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

function ServicesPanelHI() {
  const sections = [
    {
      title: "23. बिल भुगतान और डिजिटल उत्पाद नियम और शर्तें",
      content: (
        <Typography variant="body2">
          किसी भी TA वॉलेट सेवाओं का लाभ उठाने से पहले कृपया निम्नलिखित नियमों
          और शर्तों को ध्यान से पढ़ें।
        </Typography>
      ),
    },
    {
      title: "24. डिजिटल सेवाएं",
      content: (
        <Typography variant="body2">
          TA वॉलेट TA वॉलेट प्लेटफॉर्म के माध्यम से कुछ बिलों के भुगतान की
          सुविधा प्रदान करता है। इसके अलावा, TA वॉलेट कुछ डिजिटल उत्पाद भी
          प्रदान करता है जो मोबाइल, डीटीएच और डेटा कार्ड के लिए प्रीपेड रिचार्ज
          की खरीद और बस टिकटों की खरीद को सुविधाजनक बनाते हैं।
        </Typography>
      ),
    },
    {
      title: "25. TA वॉलेट रिचार्ज",
      content: (
        <Typography variant="body2">
          TA वॉलेट केवल डिजिटल उत्पादों का सुविधाकर्ता है। TA वॉलेट मोबाइल
          ऑपरेटर सेवाएं प्रदान नहीं करता है और केवल प्रीपेड मोबाइल रिचार्ज
          सेवाओं का सुविधाकर्ता है।
        </Typography>
      ),
    },
    {
      title: "26. धनवापसी नीति",
      content: (
        <Typography variant="body2">
          TA वॉलेट प्लेटफॉर्म पर प्रीपेड रिचार्ज की सभी बिक्री अंतिम है, कोई
          रिफंड या एक्सचेंज की अनुमति नहीं है। यदि आपके कार्ड या बैंक खाते से
          पैसा लिया गया है और 24 घंटों के भीतर रिचार्ज नहीं दिया जाता है, तो आप
          हमारी ग्राहक सेवा ईमेल पते पर ईमेल भेजकर हमें सूचित करें। आपको ईमेल
          प्राप्ति की तारीख से 21 कार्य दिवसों के भीतर पैसे वापस किए जाएंगे।
        </Typography>
      ),
    },
    {
      title: "27. बिल भुगतान",
      content: (
        <Typography variant="body2">
          बिल भुगतान सेवा या किसी अन्य TA वॉलेट सेवा का उपयोग करने के लिए,
          आपको वर्ल्ड वाइड वेब या इंटरनेट तक पहुंच प्राप्त करनी होगी। TA वॉलेट
          किसी भी जिम्मेदारी को स्वीकार नहीं करता और न ही कोई देयता उठाएगा यदि
          वह अपने नियंत्रण से परे परिस्थितियों के कारण भुगतान की तारीख पर किसी
          भुगतान निर्देश को प्रभावित करने में असमर्थ है।
        </Typography>
      ),
    },
  ];
  return (
    <Box>
      <SectionTitle>डिजिटल सेवाएं और बिल भुगतान</SectionTitle>
      {sections.map((s, i) => (
        <Accordion key={i}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "primary.dark", fontSize: "0.97rem" }}
            >
              {s.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1.5, pb: 2 }}>
            {s.content}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

function BusTicketsPanelHI() {
  return (
    <Box>
      <SectionTitle>28. बस टिकट</SectionTitle>
      <Typography variant="body2" gutterBottom>
        TA वॉलेट केवल एक बस टिकट एजेंट है। यह बसों का संचालन नहीं करता है या बस
        परिवहन सेवाएं प्रदान नहीं करता है। ग्राहकों को बस ऑपरेटरों, प्रस्थान
        समय और कीमतों की व्यापक पसंद प्रदान करने के लिए, TA वॉलेट ने कई बस
        ऑपरेटरों और सेवा प्रदाताओं के साथ करार किया है।
      </Typography>
      <SubTitle>TA वॉलेट की जिम्मेदारियों में शामिल हैं:</SubTitle>
      <BulletList
        items={[
          "अपने बस ऑपरेटरों के नेटवर्क के लिए एक वैध टिकट (एक टिकट जो बस ऑपरेटर द्वारा स्वीकार किया जाएगा) जारी करना;",
          "रद्दीकरण की स्थिति में धनवापसी और सहायता प्रदान करना; और",
          "किसी भी देरी/असुविधा के मामले में ग्राहक सहायता और जानकारी प्रदान करना।",
        ]}
      />
      <SubTitle>TA वॉलेट की जिम्मेदारियों में शामिल नहीं हैं:</SubTitle>
      <BulletList
        items={[
          "बस ऑपरेटर की बस समय पर न चलना/न पहुंचना;",
          "बस ऑपरेटर के कर्मचारियों का अव्यावसायिक होना या गैरकानूनी आचरण में संलग्न होना;",
          "बस ऑपरेटर की बस की सीटें आदि ग्राहक की अपेक्षाओं के अनुरूप न होना;",
          "बस ऑपरेटर किसी भी कारण से यात्रा रद्द करना;",
          "ग्राहक का सामान खो जाना/चोरी होना/क्षतिग्रस्त होना।",
        ]}
      />
      <NoteBox>
        टिकट पर उल्लिखित आगमन और प्रस्थान समय केवल अनुमानित समय हैं। हालांकि,
        बस टिकट पर उल्लिखित समय से पहले स्रोत स्थान नहीं छोड़ेगी।
      </NoteBox>
      <SubTitle>बोर्डिंग आवश्यकताएं</SubTitle>
      <BulletList
        items={[
          "टिकट की एक प्रति (टिकट का प्रिंटआउट या टिकट ईमेल का प्रिंटआउट)।",
          "पहचान प्रमाण (आधार कार्ड, ड्राइविंग लाइसेंस, छात्र आईडी कार्ड, कंपनी आईडी कार्ड, पासपोर्ट, पैन कार्ड या वोटर आईडी कार्ड)। ऐसा न करने पर, उन्हें बस में चढ़ने की अनुमति नहीं दी जा सकती है।",
        ]}
      />
    </Box>
  );
}

function UserEnvPanelHI() {
  return (
    <Box>
      <SectionTitle>29. उपयोगकर्ता परिवेश</SectionTitle>
      <SubTitle>समर्थित ब्राउज़र</SubTitle>
      <StyledTable
        headers={["ब्राउज़र", "न्यूनतम संस्करण"]}
        rows={[
          ["Chrome", "संस्करण 64 और उससे ऊपर"],
          ["Safari", "संस्करण 12.1 और उससे ऊपर"],
          ["Microsoft Edge", "संस्करण 69 और उससे ऊपर"],
          ["Firefox", "संस्करण 67 और उससे ऊपर"],
        ]}
      />
      <SubTitle>समर्थित मोबाइल प्लेटफॉर्म</SubTitle>
      <StyledTable
        headers={["प्लेटफॉर्म", "न्यूनतम संस्करण"]}
        rows={[
          ["Android (स्मार्टफोन)", "संस्करण 11.0 और उससे ऊपर"],
          ["iOS (iPhone)", "संस्करण 13 और उससे ऊपर"],
        ]}
      />
    </Box>
  );
}

// ============================================================
// KANNADA PANELS
// ============================================================
function WalletUsagePanelKN() {
  return (
    <Box>
      <SectionTitle>
        1. ವ್ಯಾಲೆಟ್ ಬಳಕೆ ನೀತಿ ಮತ್ತು ಮೊಬೈಲ್ ವ್ಯಾಲೆಟ್ ವಿತರಣೆ
      </SectionTitle>
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: "primary.dark", fontSize: "1rem" }}
          >
            01-ಮಾರ್ಚ್-2018 ರಂದು ಅಥವಾ ಅದಕ್ಕೂ ಮೊದಲು ತೆರೆಯಲಾದ ವ್ಯಾಲೆಟ್ ಗಳು
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body2" gutterBottom>
            ಪಿಪಿಐ ಕುರಿತು ಆರ್ಬಿಐ ಮಾಸ್ಟರ್ ಡೈರೆಕ್ಷನ್ಗಳ ಪ್ರಕಾರ (ಡಿಸೆಂಬರ್ 2017 ರಂತೆ),
            ಆ ದಿನದಂದು (28-ಫೆಬ್ರವರಿ-2018) ಅಸ್ತಿತ್ವದಲ್ಲಿರುವ ಎಲ್ಲಾ (ಪ್ಯಾರಾ 11)
            ಪಿಪಿಐಗಳನ್ನು ಪ್ಯಾರಾ 9 (ii) ನಲ್ಲಿ ಸೂಚಿಸಿದಂತೆ ಪೂರ್ಣ ಕೆವೈಸಿ ಪಿಪಿಐಗೆ
            ಪರಿವರ್ತಿಸಬೇಕು.
          </Typography>
          <Typography variant="body2" gutterBottom>
            ಪಿಪಿಐ ಹೊಂದಿರುವವರು ವ್ಯಾಲೆಟ್ ಅನ್ನು ಪೂರ್ಣ ಕೆವೈಸಿಗೆ ಪರಿವರ್ತಿಸಲು ಆಯ್ಕೆ
            ಮಾಡದಿದ್ದರೆ, ಅವರು ಪ್ಯಾರಾ 9.1 (ಐ) ನಲ್ಲಿ ಸೂಚಿಸಿದಂತೆ ವಿವರಗಳನ್ನು
            ಕಡ್ಡಾಯವಾಗಿ ಒದಗಿಸಬೇಕು. ಇದನ್ನು ಒದಗಿಸಿದ ನಂತರ ನೀವು ಸರಕು ಮತ್ತು ಸೇವೆಗಳ
            ಖರೀದಿಗಾಗಿ ಅಸ್ತಿತ್ವದಲ್ಲಿರುವ ಬ್ಯಾಲೆನ್ಸ್ ಬಳಸಲು ಅನುಮತಿಸಲಾಗುತ್ತದೆ.
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontStyle: "italic", my: 1, color: "#555" }}
          >
            (ಅಥವಾ)
          </Typography>
          <Typography variant="body2" gutterBottom>
            ನೀವು ಬ್ಯಾಂಕ್ ಖಾತೆ ವಿವರಗಳನ್ನು ಸಹ ನೋಂದಾಯಿಸಬಹುದು (ಇದನ್ನು ಲಾಗಿನ್
            ಆದ ನಂತರ ಪ್ರೊಫೈಲ್ ಸ್ಕ್ರೀನ್ ನಲ್ಲಿ ಮಾಡಬಹುದು) ಮತ್ತು ಮುಕ್ತಾಯವನ್ನು ಕೋರಿ
            ಬ್ಯಾಲೆನ್ಸ್ ಮೊತ್ತವನ್ನು ಬ್ಯಾಂಕ್ ಖಾತೆಗೆ ರವಾನಿಸಬಹುದು. ಅಂತಹ ವಿನಂತಿಯನ್ನು
            ನಮ್ಮ ಇ-ಮೇಲ್ ಐಡಿಗೆ ಸಲ್ಲಿಸಬಹುದು:{" "}
            <Link
              href="mailto:support@taipl.in"
              sx={{ color: "primary.main" }}
            >
              support@taipl.in
            </Link>{" "}
            (ಬ್ಯಾಂಕ್ ಪಾಸ್ ಬುಕ್ ನ ಮೊದಲ ಪುಟದೊಂದಿಗೆ). ವ್ಯಾಲೆಟ್ ಹೆಸರಿನೊಂದಿಗೆ
            ಬ್ಯಾಂಕ್ ಖಾತೆಯ ಹೆಸರನ್ನು ದೃಢೀಕರಿಸಿದ ನಂತರ ನಾವು ಮೊತ್ತವನ್ನು ಬ್ಯಾಂಕ್
            ಖಾತೆಗೆ ರವಾನಿಸುತ್ತೇವೆ. ಅಂತಹ ರವಾನೆಗೆ ಯಾವುದೇ ಶುಲ್ಕ ವಿಧಿಸಲಾಗುವುದಿಲ್ಲ.
            (ಮಾಸ್ಟರ್ ಡೈರೆಕ್ಷನ್ ಗಳ 11 (b), (c) ಮತ್ತು (d) ನೋಡಿ).
          </Typography>
          <NoteBox variant="warning">
            <strong>ಗಮನಿಸಿ:</strong> ನಿಮ್ಮ ವ್ಯಾಲೆಟ್ ಅನ್ನು ಪೂರ್ಣ ಕೆವೈಸಿಗೆ
            ಪರಿವರ್ತಿಸದಿದ್ದರೆ, ಮತ್ತಷ್ಟು ಲೋಡ್ ಮಾಡಲು ಅನುಮತಿಸಲಾಗುವುದಿಲ್ಲ ಮತ್ತು ಬಾಕಿ
            ಇರುವ ಬ್ಯಾಲೆನ್ಸ್ ಅನ್ನು ಮಾತ್ರ ಬಳಸಲು ನಿಮಗೆ ಅನುಮತಿಸಲಾಗುತ್ತದೆ.
          </NoteBox>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: "primary.dark", fontSize: "1rem" }}
          >
            01-ಮಾರ್ಚ್-2018 ರ ನಂತರ ತೆರೆಯಲಾದ ವ್ಯಾಲೆಟ್ ಗಳು
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Typography variant="body2" gutterBottom>
            ಸುತ್ತೋಲೆ ಹೊರಡಿಸಿದ ನಂತರ ತೆರೆಯಲಾದ ಕನಿಷ್ಠ ಕೆವೈಸಿ ವ್ಯಾಲೆಟ್ ಅನ್ನು ಪಿಪಿಐ
            ನೀಡಿದ ದಿನಾಂಕದಿಂದ 12 ತಿಂಗಳ ಅವಧಿಯೊಳಗೆ ಕೆವೈಸಿ ಕಾಂಪ್ಲೈಂಟ್ ಅರೆ-ಮುಚ್ಚಿದ
            ಪಿಪಿಐಗಳಾಗಿ ಪರಿವರ್ತಿಸಬೇಕು, ಇಲ್ಲದಿದ್ದರೆ ಅಂತಹ ಪಿಪಿಐಗಳಲ್ಲಿ ಮತ್ತಷ್ಟು
            ಕ್ರೆಡಿಟ್ ಗೆ ಅನುಮತಿ ಇರುವುದಿಲ್ಲ.
          </Typography>
          <Typography variant="body2" gutterBottom>
            ಆದಾಗ್ಯೂ, ಪಿಪಿಐ ಹೊಂದಿರುವವರಿಗೆ ಪಿಪಿಐ ನಲ್ಲಿ ಲಭ್ಯವಿರುವ ಬ್ಯಾಲೆನ್ಸ್
            ಬಳಸಲು ಅನುಮತಿಸಲಾಗುತ್ತದೆ ಮತ್ತು ಅವರು ತಮ್ಮ ಬ್ಯಾಂಕ್ ಖಾತೆ ವಿವರಗಳನ್ನು ಸಹ
            ನೋಂದಾಯಿಸಬಹುದು (ಇದನ್ನು ಲಾಗಿನ್ ಆದ ನಂತರ ಪ್ರೊಫೈಲ್ ವಿಭಾಗದಲ್ಲಿ
            ಮಾಡಬಹುದು) ಮತ್ತು ಮುಕ್ತಾಯವನ್ನು ಕೋರಿ ಬ್ಯಾಲೆನ್ಸ್ ಮೊತ್ತವನ್ನು ಬ್ಯಾಂಕ್
            ಖಾತೆಗೆ ರವಾನಿಸಬಹುದು. ಅಂತಹ ವಿನಂತಿಯನ್ನು ನಮ್ಮ ಇ-ಮೇಲ್ ಐಡಿಗೆ
            ಸಲ್ಲಿಸಬಹುದು:{" "}
            <Link
              href="mailto:support@taipl.in"
              sx={{ color: "primary.main" }}
            >
              support@taipl.in
            </Link>{" "}
            (ಬ್ಯಾಂಕ್ ಪಾಸ್ ಬುಕ್ ನ ಮೊದಲ ಪುಟದೊಂದಿಗೆ). ಅಂತಹ ರವಾನೆಗೆ ಯಾವುದೇ ಶುಲ್ಕ
            ವಿಧಿಸಲಾಗುವುದಿಲ್ಲ. (ಮಾಸ್ಟರ್ ಡೈರೆಕ್ಷನ್ ಗಳ 11 (b), (c) ಮತ್ತು (d) ನೋಡಿ).
          </Typography>
        </AccordionDetails>
      </Accordion>
      <NoteBox>
        <strong>ಆರ್ಬಿಐ ಪರವಾನಗಿ ಪಡೆದ ಸೆಮಿ ಕ್ಲೋಸ್ಡ್ ಪಿಪಿಐ ಆಪರೇಟರ್ ಆಗಿ</strong>{" "}
        ನಾವು ಕಾಲಕಾಲಕ್ಕೆ ಆರ್ಬಿಐ ಹೊರಡಿಸುವ ಮಾರ್ಗಸೂಚಿಗಳನ್ನು ಅನುಸರಿಸಲು
        ಬದ್ಧರಾಗಿದ್ದೇವೆ. ಗ್ರಾಹಕರು{" "}
        <Link
          href="https://transactionanalysts.com/"
          sx={{ color: "primary.main" }}
        >
          transactionanalysts.com
        </Link>{" "}
        ನಲ್ಲಿ ಲಭ್ಯವಿರುವ ಇತ್ತೀಚಿನ ಆರ್ಬಿಐ ಮಾರ್ಗಸೂಚಿಗಳನ್ನು ಮತ್ತು ಆರ್ಬಿಐ ವೆಬ್ಸೈಟ್
        ನಲ್ಲಿ "ಪಾವತಿ ಮತ್ತು ಇತ್ಯರ್ಥ ವ್ಯವಸ್ಥೆಗಳು" ಅಡಿಯಲ್ಲಿ ಸಹ ಪರಿಶೀಲಿಸಲು
        ವಿನಂತಿಸಲಾಗಿದೆ.
      </NoteBox>
      <NoteBox>
        ಯಾವುದೇ ಪ್ರಶ್ನೆಗಳಿಗೆ ಗ್ರಾಹಕರು ನಮಗೆ{" "}
        <Link
          href="mailto:support@taipl.in"
          sx={{ color: "primary.main" }}
        >
          support@taipl.in
        </Link>{" "}
        ನಲ್ಲಿ ಬರೆಯಬಹುದು ಅಥವಾ ಸಹಾಯ ಡೆಸ್ಕ್ ಸಂಖ್ಯೆಗೆ ಕರೆ ಮಾಡಬಹುದು.{" "}
        <strong>9916788339</strong>
      </NoteBox>
    </Box>
  );
}

function ProductPanelKN() {
  return (
    <Box>
      <SectionTitle>ಉತ್ಪನ್ನ</SectionTitle>
      <Typography variant="body2" gutterBottom>
        ಪಾವತಿ ಮತ್ತು ಇತ್ಯರ್ಥ ವ್ಯವಸ್ಥೆ ಕಾಯ್ದೆ 2007 ರ ಅಡಿಯಲ್ಲಿ ಅರೆ ಮುಚ್ಚಿದ ಪಿಪಿಐ
        ವ್ಯಾಲೆಟ್ ಅನ್ನು ವಿತರಿಸಲು ಆರ್ಬಿಐನಿಂದ ಪರವಾನಗಿ ಪಡೆದ ವಹಿವಾಟು ವಿಶ್ಲೇಷಕರ ಇಂಡಿಯಾ
        ಪ್ರೈವೇಟ್ ಲಿಮಿಟೆಡ್ ನಡೆಸುತ್ತಿರುವ ಟಿಎ ವ್ಯಾಲೆಟ್ ವೆಬ್ಸೈಟ್ (
        <Link href="https://www.tawallet.com" sx={{ color: "primary.main" }}>
          tawallet.com
        </Link>
        ), ಟಿಎ ವ್ಯಾಲೆಟ್ ಗೆ ಲಿಂಕ್ ಆಗಿರುವ ಎಲ್ಲಾ ಸಂಬಂಧಿತ ಸೈಟ್ ಗಳು, ಅಥವಾ ಟಿಎ
        ವ್ಯಾಲೆಟ್ ಮೊಬೈಲ್ ಅಪ್ಲಿಕೇಶನ್ ಅಥವಾ ಯಾವುದೇ ಸಮಾನ ಪ್ಲಾಟ್ ಫಾರ್ಮ್ (ಇನ್ನು ಮುಂದೆ
        ಒಟ್ಟಾರೆಯಾಗಿ "ಟಿಎ ವ್ಯಾಲೆಟ್ ಪ್ಲಾಟ್ ಫಾರ್ಮ್" ಎಂದು ಉಲ್ಲೇಖಿಸಲಾಗಿದೆ) ಅನ್ನು
        ಬಳಸುವ ಮೊದಲು ದಯವಿಟ್ಟು ಈ ಕೆಳಗಿನ ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳನ್ನು ಎಚ್ಚರಿಕೆಯಿಂದ
        ಓದಿ.
      </Typography>
      <Typography variant="body2" gutterBottom>
        ಒಬ್ಬ ನಾಗರಿಕನು ಕೇವಲ ಒಂದು ಟಿಎ ವ್ಯಾಲೆಟ್ ಅನ್ನು ಹೊಂದಬಹುದು, ಅದನ್ನು ಅವನ / ಅವಳ
        ಮೊಬೈಲ್ ಗೆ ಲಿಂಕ್ ಮಾಡಬಹುದು. ವ್ಯಾಲೆಟ್ ನಲ್ಲಿನ ಮೌಲ್ಯವನ್ನು ಟಿಎ ವ್ಯಾಲೆಟ್
        ಖಾತೆಗಳ ನಡುವೆ ಹಣ ವರ್ಗಾವಣೆ, ಬ್ಯಾಂಕ್ ಖಾತೆಗೆ ಐಎಂಪಿಎಸ್, ಟಿಎ ವ್ಯಾಲೆಟ್
        ನೊಂದಿಗೆ ನೋಂದಾಯಿಸಲಾದ ಯಾವುದೇ ಸರ್ಕಾರಿ ಇಲಾಖೆ ಅಥವಾ ವ್ಯಾಪಾರಿಗೆ ಸರಕು ಅಥವಾ
        ಸೇವೆಗಳ ಖರೀದಿಗೆ ಪಾವತಿ ಮಾಡಲು, ಮತ್ತು ಟಿಎ ವ್ಯಾಲೆಟ್ ಅಂತಹ ಸೇವೆಗಳನ್ನು
        ಒದಗಿಸಿದರೆ ಆರ್ಬಿಐ ಅನುಮತಿಸುವ ಯಾವುದೇ ಇತರ ಉದ್ದೇಶಗಳಿಗಾಗಿ ಬಳಸಬಹುದು.
      </Typography>
      <SubTitle>ಅರ್ಹತಾ ಮಾನದಂಡಗಳು</SubTitle>
      <BulletList
        items={[
          "18 ವರ್ಷ ಮತ್ತು ಅದಕ್ಕಿಂತ ಹೆಚ್ಚಿನ ವಯಸ್ಸಿನ ಭಾರತದ ಯಾವುದೇ ನಾಗರಿಕರು (ನ್ಯಾಯಾಲಯವು ಪೋಷಕರನ್ನು ನೇಮಿಸಿದರೆ 21 ವರ್ಷಗಳು) ವ್ಯಾಲೆಟ್ ಗೆ ಅರ್ಹರಾಗಿರುತ್ತಾರೆ.",
          "ನಾಗರಿಕನು ಕಾನೂನಿನಿಂದ ಅನರ್ಹಗೊಂಡ ವ್ಯಕ್ತಿಯಾಗಬಾರದು.",
          "ಬಳಕೆಯ ನಿಯಮಗಳನ್ನು ಒಪ್ಪಿಕೊಳ್ಳುವಾಗ ನಾಗರಿಕನು ಉತ್ತಮ ಮನಸ್ಸನ್ನು ಹೊಂದಿರಬೇಕು.",
          "ಒಬ್ಬ ವ್ಯಕ್ತಿಯು ಕೇವಲ ಒಂದು ವ್ಯಾಲೆಟ್ ಅನ್ನು ಮಾತ್ರ ಹೊಂದಬಹುದು ಮತ್ತು ಇದನ್ನು ಖಚಿತಪಡಿಸಿಕೊಳ್ಳುವುದು ನಾಗರಿಕರ ಜವಾಬ್ದಾರಿಯಾಗಿದೆ.",
          "ಯಾವುದೇ ಸಮಯದಲ್ಲಿ ಅನೇಕ ವ್ಯಾಲೆಟ್ ಗಳು ಕಂಡುಬಂದರೆ, ಎಲ್ಲಾ ವ್ಯಾಲೆಟ್ ಗಳಲ್ಲಿನ ಬ್ಯಾಲೆನ್ಸ್ ಗಳನ್ನು ವ್ಯಾಲೆಟ್ ಖಾತೆ ಮಿತಿ ಪರಿಶೀಲನೆಯ ಉದ್ದೇಶಕ್ಕಾಗಿ ಒಟ್ಟುಗೂಡಿಸಲಾಗುತ್ತದೆ. ಕಾಲಕಾಲಕ್ಕೆ ಆರ್ಬಿಐ ಹೊರಡಿಸುವ ಪ್ರಸ್ತುತ ಮಾರ್ಗಸೂಚಿಗಳ ಪ್ರಕಾರ ಕ್ರಮವನ್ನೂ ಪ್ರಾರಂಭಿಸಲಾಗುತ್ತದೆ.",
        ]}
      />
      <SubTitle>ವ್ಯಾಲೆಟ್ ನ ವಿಧಗಳು ಮತ್ತು ಸಾಮಾನ್ಯ ಷರತ್ತುಗಳು</SubTitle>
      <BulletList
        items={[
          "ಪ್ರಸ್ತುತ ಟಿಎ ಪೂರ್ಣ ಕೆವೈಸಿ ಮತ್ತು ಕಡಿಮೆ ಕೆವೈಸಿಯೊಂದಿಗೆ ಟಿಎ ವ್ಯಾಲೆಟ್ ಅನ್ನು ನೀಡುತ್ತಿದೆ. ಎರಡೂ ರೀತಿಯ ವ್ಯಾಲೆಟ್ ಗಳು ಮರುಲೋಡ್ ಮಾಡಬಹುದಾದ ಸ್ವಭಾವದ್ದಾಗಿದ್ದು, ದೇಶದೊಳಗಿನ ಬಳಕೆಗಾಗಿ ನೀಡಲಾಗಿದೆ.",
          "ಕಡಿಮೆ ಕೆವೈಸಿ ವ್ಯಾಲೆಟ್ ಗಳ ಸಂದರ್ಭದಲ್ಲಿ, ಇದನ್ನು ಸರಕು ಮತ್ತು ಸೇವೆಗಳ ಖರೀದಿಗೆ ಮಾತ್ರ ಬಳಸಲಾಗುತ್ತದೆ. ಅಂತಹ ಪಿಪಿಐಗಳಿಂದ ಬ್ಯಾಂಕ್ ಖಾತೆಗಳಿಗೆ ಮತ್ತು ಅದೇ/ಇತರ ವಿತರಕರ ಪಿಪಿಐಗಳಿಗೆ ಹಣ ವರ್ಗಾವಣೆಗೆ ಅನುಮತಿ ಇಲ್ಲ.",
          "ಪೂರ್ಣ ಕೆವೈಸಿ ಸಂದರ್ಭದಲ್ಲಿ ಸರಕು ಮತ್ತು ಸೇವೆಗಳ ಖರೀದಿ, ಮತ್ತು ನಿಧಿ ವರ್ಗಾವಣೆ (ಆಂತರಿಕ ಮತ್ತು ಐಎಂಪಿಎಸ್ ಎರಡೂ) ಸೌಲಭ್ಯಗಳು ಲಭ್ಯವಿದೆ.",
          "ಗ್ರಾಹಕರು ಯಾವುದೇ ಸಮಯದಲ್ಲಿ ಕಡಿಮೆ-ಕೆವೈಸಿ ವ್ಯಾಲೆಟ್ ಅನ್ನು ಪೂರ್ಣ-ಕೆವೈಸಿ ವ್ಯಾಲೆಟ್ ಆಗಿ ಪರಿವರ್ತಿಸುವ ಆಯ್ಕೆಯನ್ನು ಹೊಂದಿದ್ದಾರೆ.",
          "ವಿತರಿಸಲಾದ ಎಲ್ಲಾ ವ್ಯಾಲೆಟ್ ಗಳು ವಿತರಣೆಯ ದಿನಾಂಕದಿಂದ ಒಂದು ವರ್ಷದ ಮಾನ್ಯತೆಯ ಅವಧಿಯನ್ನು ಹೊಂದಿರುತ್ತವೆ.",
          "ಇದಲ್ಲದೆ, ಎಲ್ಲಾ ವ್ಯಾಲೆಟ್ ಗಳು ವ್ಯಾಲೆಟ್ ನಲ್ಲಿ ಕೊನೆಯ ಲೋಡಿಂಗ್/ ಮರುಲೋಡಿಂಗ್ ದಿನಾಂಕದಿಂದ ಒಂದು ವರ್ಷದ ಮಾನ್ಯತೆಯ ಅವಧಿಯನ್ನು ಹೊಂದಿರುತ್ತವೆ.",
          "ವಿತರಣೆಯ ದಿನಾಂಕದಿಂದ ಒಂದು ವರ್ಷದ ಆರಂಭಿಕ ಮಾನ್ಯತೆಯ ನಂತರ, 12 ತಿಂಗಳಿಗಿಂತ ಹೆಚ್ಚು ಕಾಲ ಕಾರ್ಯನಿರ್ವಹಿಸದ ವ್ಯಾಲೆಟ್ ಗಳನ್ನು ನಿಷ್ಕ್ರಿಯಗೊಳಿಸಲಾಗುತ್ತದೆ. ವ್ಯಾಲೆಟ್ ಅನ್ನು ಸಕ್ರಿಯವಾಗಿಡಲು ಗ್ರಾಹಕರು ಪ್ರತಿ ತಿಂಗಳು ಕನಿಷ್ಠ ಒಂದು ಆರ್ಥಿಕ ವಹಿವಾಟನ್ನು (ಡೆಬಿಟ್ ಅಥವಾ ಕ್ರೆಡಿಟ್) ಮಾಡಬೇಕೆಂದು ಕೋರಲಾಗಿದೆ.",
          "ನಿಷ್ಕ್ರಿಯ ಖಾತೆಯಾಗಿ ಒಂದು ವರ್ಷ ಪೂರ್ಣಗೊಂಡ ನಂತರ 12 ತಿಂಗಳ ಮುಕ್ತಾಯದ ಮೇಲೆ ವ್ಯಾಲೆಟ್ ಖಾತೆಯನ್ನು ಮುಕ್ತಾಯಗೊಂಡಿದೆ ಎಂದು ಪರಿಗಣಿಸಲಾಗುತ್ತದೆ.",
          "ವ್ಯಾಲೆಟ್ ಹೊಂದಿರುವವರು ಮುಕ್ತಾಯಗೊಂಡ ವ್ಯಾಲೆಟ್ ನ ಬ್ಯಾಲೆನ್ಸ್ ಮೊತ್ತದ ಮರುಪಾವತಿಗಾಗಿ ವ್ಯಾಲೆಟ್ ಮುಕ್ತಾಯ ದಿನಾಂಕದ ನಂತರ ಯಾವುದೇ ಸಮಯದಲ್ಲಿ TA ಅನ್ನು ಸಂಪರ್ಕಿಸಬಹುದು.",
          "ವ್ಯಾಲೆಟ್ ಮುಚ್ಚುವ ಸಮಯದಲ್ಲಿ ವ್ಯಾಲೆಟ್ ಬ್ಯಾಲೆನ್ಸ್ ಅನ್ನು ವರ್ಗಾಯಿಸಲು ಬಯಸುವ ಬ್ಯಾಂಕ್ ಖಾತೆ ವಿವರಗಳನ್ನು ನೋಂದಾಯಿಸಲು ವ್ಯಾಲೆಟ್ ಹೊಂದಿರುವವರನ್ನು ಕೂಡ ಕೋರಲಾಗಿದೆ. ನಮ್ಮ ಪ್ರೊಫೈಲ್ ಪುಟದಲ್ಲಿ ಬ್ಯಾಂಕ್ ಖಾತೆ ವಿವರಗಳನ್ನು ನೋಂದಾಯಿಸುವ ಸೌಲಭ್ಯವನ್ನು ನಾವು ಒದಗಿಸಿದ್ದೇವೆ.",
        ]}
      />
      <NoteBox variant="warning">
        <strong>ವಿವರಣೆ:</strong> 1.1.2025 ರಂದು ತೆರೆಯಲಾದ ವ್ಯಾಲೆಟ್ ಒಂದು ವರ್ಷದ
        ಡೀಫಾಲ್ಟ್ ಸಿಂಧುತ್ವವನ್ನು ಹೊಂದಿರುತ್ತದೆ, ಅಂದರೆ 1.1.2026 ರವರೆಗೆ
        ಮಾನ್ಯವಾಗಿರುತ್ತದೆ, 1.1.2025 ರಿಂದ 1.1.2026 ರ ಅವಧಿಯಲ್ಲಿ ವ್ಯಾಲೆಟ್
        ಹೊಂದಿರುವವರಿಂದ ಯಾವುದೇ ಕಾರ್ಯಾಚರಣೆ ನಡೆಸದಿದ್ದರೂ ಸಹ. ವ್ಯಾಲೆಟ್ ನಲ್ಲಿ ಕೊನೆಯ
        ಕಾರ್ಯಾಚರಣೆ 1.2.2026 ರಂದು ನಡೆದಿದ್ದು ನಂತರ ಯಾವುದೇ ಕಾರ್ಯಾಚರಣೆ ನಡೆಯದಿದ್ದರೆ,
        ವ್ಯಾಲೆಟ್ ಅನ್ನು 1.2.2027 ರಂದು ನಿಷ್ಕ್ರಿಯ ಎಂದು ಪರಿಗಣಿಸಲಾಗುತ್ತದೆ. 1.2.2027
        ರ ನಂತರ ವ್ಯಾಲೆಟ್ ಪರಿವರ್ತಿಸದಿದ್ದರೆ/ಸಕ್ರಿಯಗೊಳಿಸದಿದ್ದರೆ, ವ್ಯಾಲೆಟ್ ಅನ್ನು
        1.2.2028 ರಂದು ಮುಕ್ತಾಯಗೊಂಡಿದೆ ಎಂದು ಪರಿಗಣಿಸಲಾಗುತ್ತದೆ.
      </NoteBox>
      <NoteBox>
        *ಗಮನಿಸಿ: ಮೇಲೆ ತಿಳಿಸಿದ ಎಲ್ಲಾ ಮಿತಿಗಳು ಗರಿಷ್ಠ ಮಿತಿಗಳಾಗಿವೆ. ಆದಾಗ್ಯೂ,
        ಗ್ರಾಹಕರ ಅಪಾಯ ಪ್ರೊಫೈಲ್ ಆಧಾರದ ಮೇಲೆ TA ಕಡಿಮೆ ಮಿತಿಗಳನ್ನು ನಿಗದಿಪಡಿಸಬಹುದು.
      </NoteBox>
    </Box>
  );
}

function LoadingSpendingPanelKN() {
  return (
    <Box>
      <SectionTitle>ಲೋಡ್ ಆಗುತ್ತಿದೆ ಮತ್ತು ಖರ್ಚು</SectionTitle>
      <SubTitle>ಲೋಡ್ ಆಗುತ್ತಿದೆ</SubTitle>
      <Typography variant="body2" gutterBottom sx={{ mb: 1.5 }}>
        ಪ್ರಸ್ತುತ ಹಣವನ್ನು ಈ ಕೆಳಗಿನ ವಿಧಾನಗಳಲ್ಲಿ ವ್ಯಾಲೆಟ್ ಗೆ ಲೋಡ್ ಮಾಡಬಹುದು:
      </Typography>
      <BulletList
        items={[
          "ನೆಫ್ಟ್ ಸೌಲಭ್ಯವನ್ನು ಒದಗಿಸುವ ಯಾವುದೇ ಬ್ಯಾಂಕ್ ಖಾತೆಯಿಂದ ಎನ್ಇಎಫ್ಟಿ ಮೂಲಕ.",
          "ಐಎಂಪಿಎಸ್ ಸೌಲಭ್ಯವನ್ನು ಒದಗಿಸುವ ಯಾವುದೇ ಬ್ಯಾಂಕ್ ಖಾತೆಯಿಂದ ಐಎಂಪಿಎಸ್ ಮೂಲಕ.",
          "ಕ್ರೆಡಿಟ್ ಕಾರ್ಡ್ / ಡೆಬಿಟ್ ಕಾರ್ಡ್ / ನೆಟ್ ಬ್ಯಾಂಕಿಂಗ್ ನಿಂದ. (ಪೇಮೆಂಟ್ ಗೇಟ್ ವೇ)",
          "ಯುಪಿಐ ಸೌಲಭ್ಯವನ್ನು ಒದಗಿಸುವ ಯಾವುದೇ ಬ್ಯಾಂಕ್ ಖಾತೆಯಿಂದ ಯುಪಿಐ ಮೂಲಕ.",
        ]}
      />
      <SubTitle>ಖರ್ಚು</SubTitle>
      <BulletList
        items={[
          "ಸರ್ಕಾರಿ ಬಿಲ್ ಗಳು ಮತ್ತು ಸರ್ಕಾರೇತರ ಬಿಲ್ ಗಳಿಗೆ ಪಾವತಿಗಳನ್ನು ಮಾಡಲು ಟಿಎ ವ್ಯಾಲೆಟ್ ಅನ್ನು ಬಳಸಬಹುದು.",
          "ಟಿಎ ವ್ಯಾಲೆಟ್ ಅನ್ನು ಸ್ವೀಕರಿಸಲು ವಹಿವಾಟು ವಿಶ್ಲೇಷಕರಾದ ಇಂಡಿಯಾ ಪ್ರೈವೇಟ್ ಲಿಮಿಟೆಡ್ ನೊಂದಿಗೆ ಒಪ್ಪಂದ ಮಾಡಿಕೊಂಡ ವ್ಯಾಪಾರಿ ಸ್ಥಳಗಳಲ್ಲಿ.",
          "ಗ್ರಾಹಕರು ರುಪೇ ಪ್ರಿಪೇಯ್ಡ್ ಕಾರ್ಡ್ ಆಯ್ಕೆ ಮಾಡಿಕೊಂಡರೆ, ರುಪೇ ಕಾರ್ಡ್ ಸ್ವೀಕಾರಾರ್ಹವಾಗಿರುವ ವ್ಯಾಪಾರಿ ಸ್ಥಳಗಳಲ್ಲಿ.",
          "ವ್ಯಾಲೆಟ್ ನಿಂದ ಹಣವನ್ನು ಹಿಂಪಡೆಯಲು ಟಿಎ ವ್ಯಾಲೆಟ್ ಅನುಮತಿಸುವುದಿಲ್ಲ.",
        ]}
      />
      <NoteBox variant="warning">
        *ಸೂಚನೆ: ಲೋಡಿಂಗ್ ಮತ್ತು ಯಾವುದೇ ವ್ಯಾಪಾರಿ ವಹಿವಾಟಿಗೆ ಅನ್ವಯವಾಗುವ ಶುಲ್ಕಗಳನ್ನು
        ಗ್ರಾಹಕರು ಭರಿಸಬೇಕಾಗುತ್ತದೆ.
      </NoteBox>
      <SubTitle>1. ಕಡಿಮೆ ಕೆವೈಸಿ ವ್ಯಾಲೆಟ್ ಖಾತೆ ಮಿತಿಗಳು</SubTitle>
      <StyledTable
        headers={["ಸೌಲಭ್ಯ", "ಸ್ಥಿತಿ"]}
        rows={[
          ["ವ್ಯಾಲೆಟ್ ಬ್ಯಾಲೆನ್ಸ್ ಮಿತಿ", "ಯಾವುದೇ ಸಮಯದಲ್ಲಿ ರೂ.10,000/- ಮೀರಬಾರದು"],
          ["ಧನಸಹಾಯ ಮಿತಿ", "ರೂ. 10,000/- (ತಿಂಗಳಿಗೆ)"],
          ["ರೀಚಾರ್ಜ್", <AllowedChip allowed={true} />],
          ["ಬಿಲ್ ಪಾವತಿಗಳು", <AllowedChip allowed={true} />],
          ["ಟಿಕೆಟ್ ಬುಕಿಂಗ್ (ಬಸ್/ವಿಮಾನ/ಹೋಟೆಲ್)", <AllowedChip allowed={true} />],
          [
            "ವ್ಯಾಪಾರಿ ಪಾವತಿಗಳು (QR/ಬಾರ್ಕೋಡ್/OTP)",
            <AllowedChip allowed={true} />,
          ],
          ["ಇತರ ಸರಕುಗಳು ಮತ್ತು ಸೇವೆಗಳು", <AllowedChip allowed={true} />],
          ["ಇತರ ವ್ಯಾಲೆಟ್ ಗಳಿಗೆ ಹಣ ವರ್ಗಾವಣೆ", <AllowedChip allowed={false} />],
          ["ಐಎಂಪಿಎಸ್", <AllowedChip allowed={false} />],
          [
            "ಪೂರ್ಣ ಕೆವೈಸಿಗೆ ಖಾತೆ ಪರಿವರ್ತನೆ",
            "ಗ್ರಾಹಕರ ವಿನಂತಿಯ ಮೇರೆಗೆ ಆಯ್ಕೆ ಲಭ್ಯವಿದೆ",
          ],
        ]}
      />
      <SubTitle>2. ಪೂರ್ಣ ಕೆವೈಸಿ ವ್ಯಾಲೆಟ್ ಖಾತೆ ಮಿತಿಗಳು</SubTitle>
      <StyledTable
        headers={["ಸೌಲಭ್ಯ", "ಸ್ಥಿತಿ"]}
        rows={[
          [
            "ವ್ಯಾಲೆಟ್ ಬ್ಯಾಲೆನ್ಸ್ ಮಿತಿ",
            "ಯಾವುದೇ ಸಮಯದಲ್ಲಿ ರೂ.2,00,000/- ಮೀರಬಾರದು",
          ],
          ["ರೀಚಾರ್ಜ್", <AllowedChip allowed={true} />],
          ["ಬಿಲ್ ಪಾವತಿಗಳು", <AllowedChip allowed={true} />],
          ["ಟಿಕೆಟ್ ಬುಕಿಂಗ್ (ಬಸ್/ವಿಮಾನ/ಹೋಟೆಲ್)", <AllowedChip allowed={true} />],
          [
            "ವ್ಯಾಪಾರಿ ಪಾವತಿಗಳು (QR/ಬಾರ್ಕೋಡ್/OTP)",
            <AllowedChip allowed={true} />,
          ],
          ["ಇತರ ಸರಕುಗಳು ಮತ್ತು ಸೇವೆಗಳು", <AllowedChip allowed={true} />],
          ["ಇತರ ವ್ಯಾಲೆಟ್ ಗಳಿಗೆ ಹಣ ವರ್ಗಾವಣೆ*", <AllowedChip allowed={true} />],
          ["IMPS*", <AllowedChip allowed={true} />],
        ]}
      />
      <NoteBox>
        *ಗಮನಿಸಿ: ಮೇಲೆ ತಿಳಿಸಿದ ಎಲ್ಲಾ ಮಿತಿಗಳು ಗರಿಷ್ಠ ಮಿತಿಗಳಾಗಿವೆ. ಆದಾಗ್ಯೂ,
        ಗ್ರಾಹಕರ ಅಪಾಯ ಪ್ರೊಫೈಲ್ ಆಧಾರದ ಮೇಲೆ TA ಕಡಿಮೆ ಮಿತಿಗಳನ್ನು ನಿಗದಿಪಡಿಸಬಹುದು.
      </NoteBox>
    </Box>
  );
}

function ChargesPanelKN() {
  return (
    <Box>
      <SectionTitle>3. ಮಿತಿಗಳು ಮತ್ತು ಶುಲ್ಕಗಳು</SectionTitle>
      <SubTitle>ವ್ಯಾಲೆಟ್ ಬಳಕೆ ಶುಲ್ಕಗಳು</SubTitle>
      <Typography variant="body2" gutterBottom>
        ಈ ಸಾಧನದ ಬಳಕೆಗೆ ಸಂಬಂಧಿಸಿದ ಶುಲ್ಕಗಳು ಮತ್ತು ಫೀಸ್ ಗಳು ಈ ಕೆಳಗಿನಂತಿವೆ:
      </Typography>
      <StyledTable
        headers={["ಬಳಕೆ ಪ್ರಕಾರ", "ಗ್ರಾಹಕ ಶುಲ್ಕಗಳು"]}
        rows={[
          ["ವ್ಯಾಲೆಟ್ ವಿತರಣೆ", "ಯಾವುದೇ ಶುಲ್ಕಗಳಿಲ್ಲ"],
          [
            "ಆಪರೇಟಿವ್ ಖಾತೆಯನ್ನು ನಿಷ್ಕ್ರಿಯ ಖಾತೆಯಾಗಿ ಪರಿವರ್ತಿಸಲಾಗುತ್ತಿದೆ",
            "ಯಾವುದೇ ಶುಲ್ಕಗಳಿಲ್ಲ",
          ],
          [
            "ನಿಷ್ಕ್ರಿಯ ಖಾತೆಯನ್ನು ಆಪರೇಟಿವ್ ಖಾತೆಯಾಗಿ ಪರಿವರ್ತಿಸಲಾಗುತ್ತಿದೆ",
            "ಯಾವುದೇ ಶುಲ್ಕಗಳಿಲ್ಲ",
          ],
          ["ಖಾತೆ ಮುಚ್ಚುವಿಕೆ", "ಯಾವುದೇ ಶುಲ್ಕಗಳಿಲ್ಲ"],
          ["ವರ್ಚುವಲ್ ರುಪೇ ಕಾರ್ಡ್ ವಿತರಣೆ", "ಯಾವುದೇ ಶುಲ್ಕಗಳಿಲ್ಲ"],
          [
            "ಕಾರ್ಡ್ ವಿತರಣಾ ಶುಲ್ಕಗಳು ಸೇರಿದಂತೆ ಭೌತಿಕ ರುಪೇ ಕಾರ್ಡ್ ವಿತರಣೆ",
            "ರೂ.250/- ಜೊತೆಗೆ ಜಿಎಸ್ಟಿ",
          ],
          [
            "ಕಾರ್ಡ್ ವಿತರಣಾ ಶುಲ್ಕಗಳು ಸೇರಿದಂತೆ ನಕಲಿ ಭೌತಿಕ ರುಪೇ ಕಾರ್ಡ್ ವಿತರಣೆ",
            "ರೂ.250/- ಜೊತೆಗೆ ಜಿಎಸ್ಟಿ",
          ],
        ]}
      />
      <div id="load-money-charges">
        <SubTitle>ಲೋಡ್ ಮನಿ ಶುಲ್ಕಗಳು</SubTitle>

        <h4 style={{ marginTop: "20px", marginBottom: "10px" }}>PayU</h4>

        <StyledTable
          headers={["ವಹಿವಾಟು ಪ್ರಕಾರ", "ಗ್ರಾಹಕ ಶುಲ್ಕಗಳು"]}
          rows={[
            ["ಕ್ರೆಡಿಟ್ ಕಾರ್ಡ್ (CC)", "2.0% + ಜಿಎಸ್ಟಿ"],
            ["ಡೆಬಿಟ್ ಕಾರ್ಡ್ (₹2,000 ವರೆಗೆ)", "0.40% + ಜಿಎಸ್ಟಿ"],
            ["ಡೆಬಿಟ್ ಕಾರ್ಡ್ (₹2,000 ಕ್ಕಿಂತ ಹೆಚ್ಚು)", "0.85% + ಜಿಎಸ್ಟಿ"],
            ["ನೆಟ್ ಬ್ಯಾಂಕಿಂಗ್ - HDFC / ICICI", "1.6% + ಜಿಎಸ್ಟಿ"],
            ["ನೆಟ್ ಬ್ಯಾಂಕಿಂಗ್ - ಇತರ ಬ್ಯಾಂಕುಗಳು", "1.1% + ಜಿಎಸ್ಟಿ"],
          ]}
        />

        <h4 style={{ marginTop: "30px", marginBottom: "10px" }}>TAPAY</h4>

        <StyledTable
          headers={["ವಹಿವಾಟು ಪ್ರಕಾರ", "ಗ್ರಾಹಕ ಶುಲ್ಕಗಳು"]}
          rows={[
            ["ಕ್ರೆಡಿಟ್ ಕಾರ್ಡ್ (CC)", "1.94% + ಜಿಎಸ್ಟಿ"],
            ["ಡೆಬಿಟ್ ಕಾರ್ಡ್ (DC)", "0.95% + ಜಿಎಸ್ಟಿ"],
          ]}
        />

        <NoteBox>
          ಮೇಲಿನ ಶುಲ್ಕಗಳನ್ನು ಗ್ರಾಹಕರ ವ್ಯಾಲೆಟ್ ನಿಂದ ಆನ್ಲೈನ್ ಡೆಬಿಟ್ ಮಾಡಲಾಗುತ್ತದೆ.
        </NoteBox>
      </div>
      <SubTitle>ಹಣ ವರ್ಗಾವಣೆ ಶುಲ್ಕಗಳು (ಮೊತ್ತದ ಆಧಾರದ ಮೇಲೆ)</SubTitle>

      <StyledTable
        headers={["ಮೊತ್ತದ ವ್ಯಾಪ್ತಿ", "ಶುಲ್ಕಗಳು (₹)", "ಜಿಎಸ್ಟಿ (₹)", "ಒಟ್ಟು (₹)"]}
        rows={[
          ["₹0.00 - ₹1,000.00", "4.24", "0.76", "5.00"],
          ["₹1,000.01 - ₹2,000.00", "8.47", "1.53", "10.00"],
          ["₹2,000.01 - ₹5,000.00", "12.71", "2.29", "15.00"],
          ["₹5,000.01 - ₹10,000.00", "25.42", "4.58", "30.00"],
          ["₹10,000.01 - ₹15,000.00", "63.56", "11.44", "75.00"],
          ["₹15,000.01 - ₹20,000.00", "84.75", "15.25", "100.00"],
          ["₹20,000.01 - ₹25,000.00", "127.12", "22.88", "150.00"],
          ["₹25,000.01 - ₹30,000.00", "152.54", "27.46", "180.00"],
          ["₹30,000.01 - ₹35,000.00", "177.97", "32.03", "210.00"],
          ["₹35,000.01 - ₹40,000.00", "203.39", "36.61", "240.00"],
          ["₹40,000.01 - ₹45,000.00", "228.81", "41.19", "270.00"],
          ["₹45,000.01 - ₹50,000.00", "254.24", "45.76", "300.00"],
        ]}
      />

      <SubTitle>ವ್ಯಾಲೆಟ್ ಮಿತಿಗಳು</SubTitle>

      <StyledTable
        headers={["ಮಿತಿ ಪ್ರಕಾರ", "ಅವಧಿ", "ಕನಿಷ್ಠ ಕೆವೈಸಿ", "ಪೂರ್ಣ ಕೆವೈಸಿ"]}
        rows={[
          ["ಗರಿಷ್ಠ ಹೊಂದಿರುವ ಬ್ಯಾಲೆನ್ಸ್", "ಯಾವುದೇ ಸಮಯದಲ್ಲಿ", "₹10,000", "₹2,00,000"],
          [
            "ವ್ಯಾಲೆಟ್ ಗೆ ಹಣ ಸೇರಿಸುವುದು (ಇತರ ಬಳಕೆದಾರರಿಂದ ಹಣ ಸ್ವೀಕರಿಸುವುದು ಸೇರಿದಂತೆ)",
            "ತಿಂಗಳಿಗೆ",
            "₹10,000",
            "ಅಪರಿಮಿತ",
          ],
          [
            "ವ್ಯಾಲೆಟ್ ಗೆ ಹಣ ಸೇರಿಸುವುದು (ಇತರ ಬಳಕೆದಾರರಿಂದ ಹಣ ಸ್ವೀಕರಿಸುವುದು ಸೇರಿದಂತೆ)",
            "ವರ್ಷಕ್ಕೆ",
            "₹1,20,000",
            "ಅಪರಿಮಿತ",
          ],
        ]}
      />

      <SubTitle>ಲೋಡ್ ಮನಿ ಮಿತಿಗಳು</SubTitle>
      <Typography variant="body2" gutterBottom>
        ಪ್ರತಿ ವಹಿವಾಟಿಗೆ ಕನಿಷ್ಠ ಮೊತ್ತ: <strong>Rs. 10/-</strong>
      </Typography>
      <SubTitle>ಕೂಲಿಂಗ್ ಅವಧಿಗಳು</SubTitle>
      <StyledTable
        headers={["ಸಂ.ನಂ.", "ಚಟುವಟಿಕೆಯ ಸ್ವರೂಪ", "ನಿಧಿ ಬಳಕೆಗೆ ಕೂಲಿಂಗ್ ಅವಧಿ"]}
        rows={[
          ["1", "ಲೋಡಿಂಗ್ (ತೆರೆಯುವಾಗ)", "ತಕ್ಷಣ"],
          ["2", "ರೀಲೋಡಿಂಗ್", "ತಕ್ಷಣ"],
          ["3", "ಹೊಸ ಫಲಾನುಭವಿ ಸೇರ್ಪಡೆ", "ತಕ್ಷಣ"],
          ["4", "ಕಡಿಮೆ ಕೆವೈಸಿ ನೋಂದಣಿ", "ನೋಂದಣಿ ಯಶಸ್ವಿಯಾದ 4 ಗಂಟೆಗಳ ನಂತರ."],
          [
            "5",
            "ಪೂರ್ಣ ಕೆವೈಸಿ ನೋಂದಣಿ",
            "ನೋಂದಣಿ ಯಶಸ್ವಿಯಾದ 24 ಗಂಟೆಗಳ ನಂತರ ಹಣ ವರ್ಗಾವಣೆ.",
          ],
        ]}
      />
      <NoteBox variant="warning">
        ಸೂಚನೆ: ಮೇಲಿನ ಮಿತಿಗಳು (ಲೋಡ್ ಮನಿ, ಫಂಡ್ ಟ್ರಾನ್ಸ್ಫರ್) ಆರ್ಬಿಐ ನಿರ್ದೇಶನಗಳ
        ಆಧಾರದ ಮೇಲೆ ಬದಲಾಗಬಹುದು. ಯಾವುದೇ ಬದಲಾವಣೆಗಳನ್ನು ಇಲ್ಲಿ ನವೀಕರಿಸಲಾಗುತ್ತದೆ.
      </NoteBox>
    </Box>
  );
}

function PrivacyPanelKN() {
  return (
    <Box>
      <SectionTitle>2. ಗೌಪ್ಯತಾ ನೀತಿ</SectionTitle>
      <Typography variant="body2" gutterBottom>
        TA ವ್ಯಾಲೆಟ್ ನಿಮ್ಮ ನಂಬಿಕೆಯನ್ನು ಮೌಲ್ಯಯುತಗೊಳಿಸುತ್ತದೆ ಮತ್ತು ನಿಮ್ಮ
        ಗೌಪ್ಯತೆಯನ್ನು ಗೌರವಿಸುತ್ತದೆ. ಈ ಗೌಪ್ಯತಾ ನೀತಿಯು ನಿಮ್ಮ ಡೇಟಾವನ್ನು ನಾವು
        ಹೇಗೆ ಸಂಗ್ರಹಿಸುತ್ತೇವೆ, ಸಂಗ್ರಹಿಸಿಡುತ್ತೇವೆ ಮತ್ತು ಬಳಸುತ್ತೇವೆ ಎಂಬ ವಿವರಗಳನ್ನು
        ಒದಗಿಸುತ್ತದೆ. ಈ ಗೌಪ್ಯತಾ ನೀತಿಯನ್ನು ಎಚ್ಚರಿಕೆಯಿಂದ ಓದಲು ಸಲಹೆ ನೀಡಲಾಗಿದೆ. ಈ
        ನೀತಿಯನ್ನು{" "}
        <Link
          href="https://transactionanalysts.com/privacy-policy/"
          sx={{ color: "primary.main" }}
        >
          transactionanalysts.com/privacy-policy/
        </Link>{" "}
        ನಲ್ಲಿ ಇಡಲಾಗಿದೆ. ಖಾತೆ ತೆರೆಯುವಾಗ ಅಥವಾ ನಡೆಸಿದ ಯಾವುದೇ ಸಮೀಕ್ಷೆಗಳ ಸಮಯದಲ್ಲಿ
        ನಮ್ಮ ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳು ಮತ್ತು ಈ ನೀತಿಯನ್ನು ನೀವು ಸ್ವೀಕರಿಸುವ ಮೂಲಕ, ಈ
        ಗೌಪ್ಯತಾ ನೀತಿಗೆ ಅನುಗುಣವಾಗಿ ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಮಾಹಿತಿಯನ್ನು ಬಳಸಲು ಮತ್ತು
        ಬಹಿರಂಗಪಡಿಸಲು ನೀವು ಸ್ಪಷ್ಟವಾಗಿ ನಮಗೆ ಒಪ್ಪಿಗೆ ನೀಡುತ್ತೀರಿ.
      </Typography>
      <NoteBox variant="warning">
        ನಮ್ಮ ಗೌಪ್ಯತಾ ನೀತಿಯು ಯಾವುದೇ ಸಮಯದಲ್ಲಿ ಬದಲಾಗಬಹುದು ಮತ್ತು ಯಾವುದೇ
        ಬದಲಾವಣೆಗಳನ್ನು ನಮ್ಮ ಮೇಲಿನ ವೆಬ್ಸೈಟ್ ನಲ್ಲಿ ನವೀಕರಿಸಲಾಗುತ್ತದೆ. ಆದ್ದರಿಂದ
        ಯಾವುದೇ ಬದಲಾವಣೆಗಳ ಬಗ್ಗೆ ನಿಮಗೆ ತಿಳಿದಿದೆ ಎಂದು ಖಚಿತಪಡಿಸಿಕೊಳ್ಳಲು, ದಯವಿಟ್ಟು
        ನೀತಿಯನ್ನು ಪರಿಶೀಲಿಸಲು ನಿಯತಕಾಲಿಕವಾಗಿ ನಮ್ಮ ವೆಬ್ಸೈಟ್ ಗೆ ಭೇಟಿ ನೀಡಿ.
      </NoteBox>
      <Typography variant="body2" gutterBottom>
        ನಾವು ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಮಾಹಿತಿಯನ್ನು ಯಾವುದೇ ಮೂರನೇ ಪಕ್ಷಕ್ಕೆ ಮಾರಾಟ, ಹಂಚಿಕೆ ಅಥವಾ
        ಬಾಡಿಗೆಗೆ ನೀಡುವುದಿಲ್ಲ ಅಥವಾ ಅಹಿತಕರ ಇ-ಮೇಲ್ ಗಳು ಮತ್ತು/ಅಥವಾ ಎಸ್ಎಂಎಸ್
        ಗಳಿಗಾಗಿ ನಿಮ್ಮ ಇ-ಮೇಲ್ ವಿಳಾಸ/ಮೊಬೈಲ್ ಸಂಖ್ಯೆ ಬಳಸುವುದಿಲ್ಲ. ನಾವು ಕಳುಹಿಸುವ
        ಯಾವುದೇ ಇ-ಮೇಲ್ ಗಳು ಮತ್ತು/ಅಥವಾ ಎಸ್ಎಂಎಸ್ ಗಳು ಒಪ್ಪಿದ ಸೇವೆಗಳು ಮತ್ತು
        ಉತ್ಪನ್ನಗಳ ಒದಗಿಸುವಿಕೆ ಮತ್ತು ಈ ಗೌಪ್ಯತಾ ನೀತಿಗೆ ಸಂಬಂಧಿಸಿದಂತೆ ಮಾತ್ರ
        ಇರುತ್ತವೆ.
      </Typography>
      <Typography variant="body2" gutterBottom>
        ವೈಯಕ್ತಿಕ ಮಾಹಿತಿ ಎಂದರೆ ಮತ್ತು ಇದು ನಿರ್ದಿಷ್ಟ ವ್ಯಕ್ತಿಗೆ ಸಂಬಂಧಿಸಬಹುದಾದ
        ಅಥವಾ ಯಾವುದೇ ವ್ಯಕ್ತಿಯನ್ನು ಗುರುತಿಸಬಹುದಾದ ಎಲ್ಲಾ ಮಾಹಿತಿಯನ್ನು ಒಳಗೊಂಡಿದೆ,
        ಉದಾಹರಣೆಗೆ ಹೆಸರು, ವಿಳಾಸ, ಅಂಚೆ ವಿಳಾಸ, ದೂರವಾಣಿ ಸಂಖ್ಯೆ, ಇಮೇಲ್ ಐಡಿ, ಕ್ರೆಡಿಟ್
        ಕಾರ್ಡ್ ಸಂಖ್ಯೆ, ಕಾರ್ಡ್ ಹೊಂದಿರುವವರ ಹೆಸರು, ಕಾರ್ಡ್ ಮುಕ್ತಾಯ ದಿನಾಂಕ, ನಿಮ್ಮ
        ಮೊಬೈಲ್ ಫೋನ್ ಬಗ್ಗೆ ಮಾಹಿತಿ ಮತ್ತು ಬಳಕೆದಾರರು TA ವ್ಯಾಲೆಟ್ ನಲ್ಲಿ ಯಾವುದೇ
        ಸೇವೆಗಳನ್ನು ಪಡೆಯುವ ಸಂಬಂಧದಲ್ಲಿ ಸ್ವಯಂಪ್ರೇರಣೆಯಿಂದ ಒದಗಿಸಿರುವ ಯಾವುದೇ
        ವಿವರಗಳು.
      </Typography>
      <Typography variant="body2" gutterBottom>
        ನಮ್ಮ ನಿಯಂತ್ರಣದಲ್ಲಿರುವ ಮಾಹಿತಿಯ ಕಳೆದುಹೋಗುವಿಕೆ, ದುರ್ಬಳಕೆ ಮತ್ತು ಮಾರ್ಪಾಡಿನಿಂದ
        ರಕ್ಷಿಸಲು ನಮ್ಮಲ್ಲಿ ಕಠಿಣ ಭದ್ರತಾ ಕ್ರಮಗಳಿವೆ. ನೀವು ನಮ್ಮ ಅಪ್ಲಿಕೇಶನ್ ಗೆ
        ಲಾಗಿನ್ ಆದ ನಂತರ, ನಾವು ಸುರಕ್ಷಿತ ಸರ್ವರ್ ಬಳಕೆಯನ್ನು ನೀಡುತ್ತೇವೆ.
      </Typography>
    </Box>
  );
}

function TermsPanelKN() {
  const sections = [
    {
      title: "ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳು (ಸಾಮಾನ್ಯ)",
      content: (
        <>
          <Typography variant="body2" gutterBottom>
            ಈ ವೆಬ್ಸೈಟ್ ಅನ್ನು ಟ್ರಾನ್ಸಾಕ್ಷನ್ ಅನಾಲಿಸ್ಟ್ಸ್ ಇಂಡಿಯಾ ಪ್ರೈವೇಟ್ ಲಿಮಿಟೆಡ್
            (ಇನ್ನು ಮುಂದೆ ಒಟ್ಟಾರೆಯಾಗಿ "TA" ಎಂದು ಉಲ್ಲೇಖಿಸಲಾಗಿದೆ) ವಿನ್ಯಾಸಗೊಳಿಸಿದೆ,
            ಅಭಿವೃದ್ಧಿಪಡಿಸಿದೆ ಮತ್ತು ನಿರ್ವಹಿಸುತ್ತದೆ.
          </Typography>
          <Typography variant="body2" gutterBottom>
            ಈ ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳನ್ನು ಭಾರತೀಯ ಕಾನೂನುಗಳಿಗೆ ಅನುಗುಣವಾಗಿ
            ನಿಯಂತ್ರಿಸಲಾಗುತ್ತದೆ ಮತ್ತು ವ್ಯಾಖ್ಯಾನಿಸಲಾಗುತ್ತದೆ. ಈ ನಿಯಮಗಳು ಮತ್ತು
            ಷರತ್ತುಗಳ ಅಡಿಯಲ್ಲಿ ಉದ್ಭವಿಸುವ ಯಾವುದೇ ವಿವಾದವು ಭಾರತದ ನ್ಯಾಯಾಲಯಗಳ
            ಅಧಿಕಾರ ವ್ಯಾಪ್ತಿಗೆ ಒಳಪಟ್ಟಿರುತ್ತದೆ.
          </Typography>
        </>
      ),
    },
    {
      title: "3. ಒಪ್ಪಿಗೆ",
      content: (
        <Typography variant="body2">
          ಖಾತೆ ತೆರೆಯುವಾಗ ನಮ್ಮ ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳನ್ನು ಮತ್ತು ಗೌಪ್ಯತಾ ನೀತಿಯನ್ನು
          ಸ್ವೀಕರಿಸುವ ಮೂಲಕ, ಈ ಗೌಪ್ಯತಾ ನೀತಿಗೆ ಅನುಗುಣವಾಗಿ ನೀವು ಬಹಿರಂಗಪಡಿಸುವ ಮಾಹಿತಿಯ
          ಸಂಗ್ರಹ ಮತ್ತು ಬಳಕೆಗೆ ನೀವು ಒಪ್ಪಿಗೆ ನೀಡುತ್ತೀರಿ.
        </Typography>
      ),
    },
    {
      title: "4. ಸ್ವೀಕೃತಿ",
      content: (
        <Typography variant="body2">
          TA ವ್ಯಾಲೆಟ್ ಪ್ಲಾಟ್ ಫಾರ್ಮ್ ಅನ್ನು ನೋಂದಾಯಿಸುವ, ಪ್ರವೇಶಿಸುವ, ಬ್ರೌಸ್ ಮಾಡುವ,
          ಡೌನ್ಲೋಡ್ ಮಾಡುವ ಅಥವಾ ಬಳಸುವ ಮೂಲಕ, ಕೆಳಗೆ ತಿಳಿಸಿರುವ ಏಕ-ಬಳಕೆದಾರ-ಐಡಿ ಮತ್ತು
          ಪಾಸ್ ವರ್ಡ್ ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳಿಗೆ ನೀವು ಬದ್ಧರಾಗಲು ಒಪ್ಪುತ್ತೀರಿ.
        </Typography>
      ),
    },
    {
      title: "5. ಅರ್ಹತೆ",
      content: (
        <Typography variant="body2">
          18 ವರ್ಷಕ್ಕಿಂತ ಕಡಿಮೆ ವಯಸ್ಸಿನ ವ್ಯಕ್ತಿಗಳಿಗೆ ಅಥವಾ ಟಿಎ ವ್ಯಾಲೆಟ್ ಹಿಂದೆ
          ಅಮಾನತು ಮಾಡಿದ ಅಥವಾ ತೆಗೆದುಹಾಕಿದ ವ್ಯಕ್ತಿಗಳಿಗೆ TA ವ್ಯಾಲೆಟ್ ಸೇವೆಗಳು
          ಲಭ್ಯವಿಲ್ಲ.
        </Typography>
      ),
    },
    {
      title: "6. ಇತರ ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳು",
      content: (
        <Typography variant="body2">
          TA ವ್ಯಾಲೆಟ್ ಯಾವುದೇ ಸಮಯದಲ್ಲಿ, ಯಾವುದೇ ಸೂಚನೆಯಿಲ್ಲದೆ, TA ವ್ಯಾಲೆಟ್ ಪ್ಲಾಟ್
          ಫಾರ್ಮ್ ನಲ್ಲಿ ನೀಡಲಾಗುವ ಯಾವುದೇ TA ವ್ಯಾಲೆಟ್ ಸೇವೆಗಳಿಗೆ, ಅಥವಾ ಅಂತಹ
          ಸೇವೆಗಳಿಗೆ ಅನ್ವಯವಾಗುವ ನಿಯಮಗಳಿಗೆ ಬದಲಾವಣೆಗಳನ್ನು ಮಾಡಬಹುದು.
        </Typography>
      ),
    },
    {
      title: "7. ಸಂವಹನ ನೀತಿ",
      content: (
        <Typography variant="body2">
          T&C ಗಳನ್ನು ಸ್ವೀಕರಿಸುವ ಮೂಲಕ, TA ವ್ಯಾಲೆಟ್ ನೀವು ಸೇವೆಗಾಗಿ TA ವ್ಯಾಲೆಟ್
          ಪ್ಲಾಟ್ ಫಾರ್ಮ್ ನಲ್ಲಿ ನೋಂದಾಯಿಸುವಾಗ ಒದಗಿಸಿದ ಮೊಬೈಲ್ ಫೋನ್ ಸಂಖ್ಯೆಗೆ, ಅಥವಾ
          ನಂತರ ನಮ್ಮಿಂದ TA ವ್ಯಾಲೆಟ್ ಪ್ಲಾಟ್ ಫಾರ್ಮ್ ನಲ್ಲಿ ದಾಖಲಿಸಲಾದ ಯಾವುದೇ
          ನವೀಕರಿಸಿದ ಮೊಬೈಲ್ ಸಂಖ್ಯೆಗೆ, ಅಥವಾ ಇ-ಮೇಲ್ ಅಥವಾ ಪುಶ್ ನೋಟಿಫಿಕೇಶನ್ ಮೂಲಕ
          ಎಚ್ಚರಿಕೆಗಳನ್ನು ಕಳುಹಿಸಬಹುದು ಎಂದು ನೀವು ಒಪ್ಪುತ್ತೀರಿ.
        </Typography>
      ),
    },
    {
      title: "8. TA ವ್ಯಾಲೆಟ್ ಪ್ಲಾಟ್ ಫಾರ್ಮ್ ಬಳಕೆ",
      content: (
        <Typography variant="body2">
          TA ವ್ಯಾಲೆಟ್ ಸ್ಪಷ್ಟವಾಗಿ ಒದಗಿಸಿದ ಮಾಹಿತಿ, ಉತ್ಪನ್ನಗಳು ಅಥವಾ ಸೇವೆಗಳನ್ನು
          ಹೊರತುಪಡಿಸಿ, ನಾವು ಇಂಟರ್ನೆಟ್ ನಲ್ಲಿ ಯಾವುದೇ ಮಾಹಿತಿ, ಉತ್ಪನ್ನಗಳು ಅಥವಾ
          ಸೇವೆಗಳನ್ನು ಯಾವುದೇ ರೀತಿಯಲ್ಲಿ ನಿರ್ವಹಿಸುವುದಿಲ್ಲ, ನಿಯಂತ್ರಿಸುವುದಿಲ್ಲ
          ಅಥವಾ ಅನುಮೋದಿಸುವುದಿಲ್ಲ ಎಂದು ನೀವು ಅರ್ಥಮಾಡಿಕೊಳ್ಳುತ್ತೀರಿ.
        </Typography>
      ),
    },
    {
      title: "9. ನಿಷಿದ್ಧ ನಡವಳಿಕೆ",
      content: (
        <Typography variant="body2" gutterBottom>
          TA ವ್ಯಾಲೆಟ್ ಪ್ಲಾಟ್ ಫಾರ್ಮ್ ಅನ್ನು ಪ್ರವೇಶಿಸುವ ಅಥವಾ ಬಳಸುವ ಮೂಲಕ, ಅಥವಾ TA
          ವ್ಯಾಲೆಟ್ ಸೇವೆಗಳನ್ನು ಪಡೆಯುವ ಮೂಲಕ, ನೀವು T&C ಗಳನ್ನು ಉಲ್ಲಂಘಿಸದಿರಲು,
          ಯಾವುದೇ ವ್ಯಕ್ತಿ ಅಥವಾ ಘಟಕವನ್ನು ಅನುಕರಿಸದಿರಲು, ಬೌದ್ಧಿಕ ಆಸ್ತಿ ಹಕ್ಕುಗಳನ್ನು
          ಉಲ್ಲಂಘಿಸದಿರಲು, 18 ವರ್ಷಕ್ಕಿಂತ ಕಡಿಮೆ ವಯಸ್ಸಿನವರಾಗಿದ್ದರೆ TA ವ್ಯಾಲೆಟ್
          ಸೇವೆಗಳನ್ನು ಬಳಸದಿರಲು, ಆಕ್ಷೇಪಾರ್ಹ ವಿಷಯವನ್ನು ಪೋಸ್ಟ್ ಮಾಡದಿರಲು, ಅಥವಾ
          ಯಾವುದೇ ಮೋಸದ ಚಟುವಟಿಕೆಯಲ್ಲಿ ತೊಡಗಿಸಿಕೊಳ್ಳದಿರಲು ಒಪ್ಪುತ್ತೀರಿ.
        </Typography>
      ),
    },
    {
      title: "10. ಮುಕ್ತಾಯ; ಒಪ್ಪಂದದ ಉಲ್ಲಂಘನೆಗಳು",
      content: (
        <Typography variant="body2">
          ನೀವು ಈ ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳನ್ನು ಉಲ್ಲಂಘಿಸಿದರೆ, TA ವ್ಯಾಲೆಟ್ ಯಾವುದೇ
          ಸಮಯದಲ್ಲಿ, ಯಾವುದೇ ಸೂಚನೆಯಿಲ್ಲದೆ, ತನ್ನ ವಿವೇಚನೆಯಿಂದ ನಿಮ್ಮ ಸೇವೆಗಳ
          ಪ್ರವೇಶವನ್ನು ಅಮಾನತುಗೊಳಿಸಬಹುದು ಅಥವಾ ಕೊನೆಗೊಳಿಸಬಹುದು.
        </Typography>
      ),
    },
    {
      title: "11. ಹೊಣೆಗಾರಿಕೆ ಮತ್ತು ಹಾನಿಗಳ ಮಿತಿ",
      content: (
        <Typography variant="body2">
          ಯಾವುದೇ ಸಂದರ್ಭದಲ್ಲಿ, TA ವ್ಯಾಲೆಟ್ ಅಥವಾ ಅದರ ಗುತ್ತಿಗೆದಾರರು, ಏಜೆಂಟರು,
          ಪರವಾನಗಿದಾರರು, ಪಾಲುದಾರರು ಅಥವಾ ಪೂರೈಕೆದಾರರು ನಿಮಗೆ ಯಾವುದೇ ವಿಶೇಷ, ಪರೋಕ್ಷ,
          ಪ್ರಾಸಂಗಿಕ, ಪರಿಣಾಮಕಾರಿ ಹಾನಿಗಳಿಗೆ ಹೊಣೆಗಾರರಾಗಿರುವುದಿಲ್ಲ, ಇದು ರೂ. 1,000
          ಮೀರುವುದಿಲ್ಲ, ಇವೆರಡರಲ್ಲಿ ಯಾವುದು ಕಡಿಮೆಯೋ ಅದು.
        </Typography>
      ),
    },
    {
      title: "12. ನಷ್ಟ ಪರಿಹಾರ",
      content: (
        <Typography variant="body2">
          ನೀವು TA ವ್ಯಾಲೆಟ್, ಅದರ ಅಂಗಸಂಸ್ಥೆಗಳು, ಗುತ್ತಿಗೆದಾರರು, ಉದ್ಯೋಗಿಗಳು,
          ಅಧಿಕಾರಿಗಳು, ನಿರ್ದೇಶಕರು, ಏಜೆಂಟರು ಮತ್ತು ಅದರ ಮೂರನೇ ವ್ಯಕ್ತಿ
          ಪೂರೈಕೆದಾರರು, ಪರವಾನಗಿದಾರರು ಮತ್ತು ಪಾಲುದಾರರನ್ನು ಯಾವುದೇ ಮತ್ತು ಎಲ್ಲಾ
          ಹಕ್ಕುಗಳು, ನಷ್ಟಗಳು, ಹಾನಿಗಳು ಮತ್ತು ಹೊಣೆಗಾರಿಕೆಗಳಿಂದ ನಷ್ಟ ಪರಿಹಾರ ನೀಡಲು,
          ಉಳಿಸಲು ಮತ್ತು ಹಾನಿರಹಿತವಾಗಿ ಇರಿಸಲು ಒಪ್ಪುತ್ತೀರಿ.
        </Typography>
      ),
    },
    {
      title: "13. ಹಕ್ಕುತ್ಯಾಗ; ಯಾವುದೇ ಖಾತರಿಗಳಿಲ್ಲ",
      content: (
        <Typography variant="body2">
          ಅನ್ವಯವಾಗುವ ಕಾನೂನಿನ ಪ್ರಕಾರ ಅನುಮತಿಸುವ ಸಂಪೂರ್ಣ ಮಟ್ಟಿಗೆ, TA ವ್ಯಾಲೆಟ್
          ಮತ್ತು ಅದರ ಮೂರನೇ ವ್ಯಕ್ತಿ ಪಾಲುದಾರರು, ಪರವಾನಗಿದಾರರು ಮತ್ತು ಪೂರೈಕೆದಾರರು
          ಎಲ್ಲಾ ಖಾತರಿಗಳು ಅಥವಾ ಗ್ಯಾರಂಟಿಗಳನ್ನು ನಿರಾಕರಿಸುತ್ತಾರೆ - ಶಾಸನಬದ್ಧ,
          ಸ್ಪಷ್ಟ ಅಥವಾ ಸೂಚಿತವಾಗಿರಲಿ.
        </Typography>
      ),
    },
    {
      title: "14. ಮಾಲೀಕತ್ವ; ಸ್ವಾಮ್ಯಸೂಚಕ ಹಕ್ಕುಗಳು",
      content: (
        <Typography variant="body2">
          TA ವ್ಯಾಲೆಟ್ ಸೇವೆಗಳು ಮತ್ತು TA ವ್ಯಾಲೆಟ್ ಪ್ಲಾಟ್ ಫಾರ್ಮ್ TA ವ್ಯಾಲೆಟ್
          ಮತ್ತು/ಅಥವಾ ಮೂರನೇ ವ್ಯಕ್ತಿ ಪರವಾನಗಿದಾರರ ಒಡೆತನದಲ್ಲಿ ಮತ್ತು ನಿರ್ವಹಣೆಯಲ್ಲಿವೆ.
          ಎಲ್ಲಾ ವಸ್ತುಗಳು, ಟ್ರೇಡ್ ಮಾರ್ಕ್ ಗಳು, ಸೇವಾ ಗುರುತುಗಳು ಮತ್ತು ವ್ಯಾಪಾರ
          ಹೆಸರುಗಳು TA ವ್ಯಾಲೆಟ್ ನ ಆಸ್ತಿಯಾಗಿವೆ.
        </Typography>
      ),
    },
    {
      title: "15. ಈ ಒಪ್ಪಂದದ ಮಾರ್ಪಾಡು",
      content: (
        <Typography variant="body2">
          TA ವ್ಯಾಲೆಟ್ ಪ್ಲಾಟ್ ಫಾರ್ಮ್ ನಲ್ಲಿ ಅಥವಾ ನಮ್ಮ ವೆಬ್ಸೈಟ್ ನಲ್ಲಿ
          ಅಧಿಸೂಚನೆಯನ್ನು ಪೋಸ್ಟ್ ಮಾಡುವ ಮೂಲಕ ಯಾವುದೇ ಸಮಯದಲ್ಲಿ ಈ ಒಪ್ಪಂದದ
          ಭಾಗಗಳನ್ನು ಬದಲಾಯಿಸುವ, ಮಾರ್ಪಡಿಸುವ, ಸೇರಿಸುವ ಅಥವಾ ತೆಗೆದುಹಾಕುವ ಹಕ್ಕನ್ನು
          TA ವ್ಯಾಲೆಟ್ ಕಾಯ್ದಿರಿಸಿದೆ.
        </Typography>
      ),
    },
    {
      title: "16. ಸೂಚನೆ",
      content: (
        <Typography variant="body2">
          TA ವ್ಯಾಲೆಟ್ ನಿಮಗೆ ಇ-ಮೇಲ್, ಎಸ್ಎಂಎಸ್, ಪುಶ್ ಅಧಿಸೂಚನೆಗಳು, ನಿಯಮಿತ ಮೇಲ್
          ಅಥವಾ TA ವ್ಯಾಲೆಟ್ ಪ್ಲಾಟ್ ಫಾರ್ಮ್ ನಲ್ಲಿ ಪೋಸ್ಟಿಂಗ್ ಗಳ ಮೂಲಕ ಅಥವಾ ಯಾವುದೇ
          ಇತರ ಸಮಂಜಸ ವಿಧಾನಗಳ ಮೂಲಕ ಸೂಚನೆಗಳು ಮತ್ತು ಸಂವಹನಗಳನ್ನು ಒದಗಿಸಬಹುದು.
        </Typography>
      ),
    },
    {
      title: "17. ಮನ್ನಾ",
      content: (
        <Typography variant="body2">
          ಈ ಒಪ್ಪಂದದ ಯಾವುದೇ ಹಕ್ಕು ಅಥವಾ ನಿಬಂಧನೆಯನ್ನು ಚಲಾಯಿಸಲು ಅಥವಾ ಜಾರಿಗೊಳಿಸಲು
          TA ವ್ಯಾಲೆಟ್ ವಿಫಲವಾದರೆ ಅಂತಹ ಹಕ್ಕು ಅಥವಾ ನಿಬಂಧನೆಯ ಮನ್ನಾವನ್ನು
          ರೂಪಿಸುವುದಿಲ್ಲ.
        </Typography>
      ),
    },
    {
      title: "18. ವಿವಾದ ಪರಿಹಾರ",
      content: (
        <Typography variant="body2">
          ಈ ಒಪ್ಪಂದದ ಅಡಿಯಲ್ಲಿ ಯಾವುದೇ ವಿವಾದ ಉದ್ಭವಿಸಿದರೆ, ಪಕ್ಷಗಳು ಅದನ್ನು
          ಸೌಹಾರ್ದಯುತವಾಗಿ ಪರಿಹರಿಸಲು ಪ್ರಯತ್ನಿಸಬೇಕು. ಮಧ್ಯಸ್ಥಿಕೆಯ ಪೀಠವು ಹೈದರಾಬಾದ್
          ಆಗಿರುತ್ತದೆ ಮತ್ತು ಭಾಷೆ ಇಂಗ್ಲಿಷ್ ಆಗಿರುತ್ತದೆ.
        </Typography>
      ),
    },
    {
      title: "19. ವಿಭಜನೀಯತೆ",
      content: (
        <Typography variant="body2">
          ಈ ಒಪ್ಪಂದದ ಯಾವುದೇ ನಿಬಂಧನೆಯನ್ನು ಕಾನೂನುಬಾಹಿರ, ಅಸಿಂಧು, ಅಮಾನ್ಯ ಅಥವಾ
          ಜಾರಿಗೊಳಿಸಲಾಗದು ಎಂದು ಪರಿಗಣಿಸಿದರೆ, ಆ ನಿಬಂಧನೆಯನ್ನು ಅಗತ್ಯವಿರುವ ಕನಿಷ್ಠ
          ಮಟ್ಟಿಗೆ ಸೀಮಿತಗೊಳಿಸಲಾಗುತ್ತದೆ ಅಥವಾ ಈ ಒಪ್ಪಂದದಿಂದ ತೆಗೆದುಹಾಕಲಾಗುತ್ತದೆ.
        </Typography>
      ),
    },
    {
      title: "20. ನಿಯೋಜನೆ",
      content: (
        <Typography variant="body2">
          ಈ ಒಪ್ಪಂದ, ಮತ್ತು ಇಲ್ಲಿ ನೀಡಲಾದ ಯಾವುದೇ ಹಕ್ಕುಗಳನ್ನು, TA ವ್ಯಾಲೆಟ್ ನ
          ಪೂರ್ವ ಲಿಖಿತ ಒಪ್ಪಿಗೆಯಿಲ್ಲದೆ ನಿಮ್ಮಿಂದ ವರ್ಗಾಯಿಸಲಾಗುವುದಿಲ್ಲ ಅಥವಾ
          ನಿಯೋಜಿಸಲಾಗುವುದಿಲ್ಲ.
        </Typography>
      ),
    },
    {
      title: "21. ಉಳಿಯುವಿಕೆ",
      content: (
        <Typography variant="body2">
          ಈ ಒಪ್ಪಂದದ ಮುಕ್ತಾಯದ ಮೇಲೆ, ಅದರ ಸ್ವಭಾವ ಅಥವಾ ಸ್ಪಷ್ಟ ನಿಬಂಧನೆಗಳ ಪ್ರಕಾರ
          ಉಳಿಯಬೇಕಾದ ಯಾವುದೇ ನಿಬಂಧನೆಯು ಅಂತಹ ಮುಕ್ತಾಯ ಅಥವಾ ಅವಧಿ ಮುಗಿದ ನಂತರವೂ
          ಉಳಿಯುತ್ತದೆ.
        </Typography>
      ),
    },
    {
      title: "22. ಸಂಪೂರ್ಣ ಒಪ್ಪಂದ",
      content: (
        <Typography variant="body2">
          ಈ ಒಪ್ಪಂದವು ಇದರ ವಿಷಯಕ್ಕೆ ಸಂಬಂಧಿಸಿದಂತೆ ನಿಮ್ಮ ಮತ್ತು ಟಿಎ ವ್ಯಾಲೆಟ್ ನಡುವಿನ
          ಸಂಪೂರ್ಣ ಒಪ್ಪಂದವಾಗಿದೆ.
        </Typography>
      ),
    },
  ];
  return (
    <Box>
      <SectionTitle>ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳು</SectionTitle>
      {sections.map((s, i) => (
        <Accordion key={i}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "primary.dark", fontSize: "0.97rem" }}
            >
              {s.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1.5, pb: 2 }}>
            {s.content}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

function ServicesPanelKN() {
  const sections = [
    {
      title:
        "23. ಬಿಲ್ ಪಾವತಿಗಳು ಮತ್ತು ಡಿಜಿಟಲ್ ಉತ್ಪನ್ನಗಳ ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳು",
      content: (
        <Typography variant="body2">
          ಯಾವುದೇ TA ವ್ಯಾಲೆಟ್ ಸೇವೆಗಳನ್ನು ಪಡೆಯುವ ಮೊದಲು TA ವ್ಯಾಲೆಟ್ ಪ್ಲಾಟ್ ಫಾರ್ಮ್
          ನಲ್ಲಿ ದಯವಿಟ್ಟು ಈ ಕೆಳಗಿನ ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳನ್ನು ಎಚ್ಚರಿಕೆಯಿಂದ ಓದಿ.
        </Typography>
      ),
    },
    {
      title: "24. ಡಿಜಿಟಲ್ ಸೇವೆಗಳು",
      content: (
        <Typography variant="body2">
          ಟಿಎ ವ್ಯಾಲೆಟ್ ಪ್ಲಾಟ್ ಫಾರ್ಮ್ ಮೂಲಕ ತಮ್ಮ ಬಿಲ್ ಗಳನ್ನು ಪಾವತಿಸಲು ಅನುವು
          ಮಾಡಿಕೊಡಲು ಟಿಎ ವ್ಯಾಲೆಟ್ ಕೆಲವು ಬಿಲ್ ಗಳನ್ನು ಪಾವತಿಸಲು ಅನುಕೂಲ
          ಮಾಡಿಕೊಡುತ್ತದೆ. ಇದಲ್ಲದೆ, ಟಿಎ ವ್ಯಾಲೆಟ್ ಮೊಬೈಲ್, ಡಿಟಿಎಚ್ ಮತ್ತು ಡೇಟಾ ಕಾರ್ಡ್
          ಗಾಗಿ ಪ್ರಿಪೇಯ್ಡ್ ರೀಚಾರ್ಜ್ ಗಳನ್ನು ಖರೀದಿಸಲು ಮತ್ತು ಬಸ್ ಟಿಕೆಟ್ ಗಳ ಖರೀದಿಗೆ
          ಅನುಕೂಲ ಮಾಡಿಕೊಡುತ್ತದೆ.
        </Typography>
      ),
    },
    {
      title: "25. ಟಿಎ ವ್ಯಾಲೆಟ್ ರೀಚಾರ್ಜ್",
      content: (
        <Typography variant="body2">
          ಟಿಎ ವ್ಯಾಲೆಟ್ ಗಳು ಡಿಜಿಟಲ್ ಉತ್ಪನ್ನಗಳ ಆಯೋಜಕ ಮಾತ್ರ. ಟಿಎ ವ್ಯಾಲೆಟ್ ಮೊಬೈಲ್
          ಆಪರೇಟರ್ ಸೇವೆಗಳನ್ನು ಒದಗಿಸುವುದಿಲ್ಲ ಮತ್ತು ಪ್ರಿಪೇಯ್ಡ್ ಮೊಬೈಲ್ ರೀಚಾರ್ಜ್
          ಸೇವೆಗಳ ಆಯೋಜಕ ಮಾತ್ರ. ಖರೀದಿಸಿದ ಮೊಬೈಲ್ ಪ್ರಿಪೇಯ್ಡ್ ರೀಚಾರ್ಜ್ ನ ಗುಣಮಟ್ಟ,
          ನಿಮಿಷಗಳು, ವೆಚ್ಚಕ್ಕೆ ಸಂಬಂಧಿಸಿದ ಯಾವುದೇ ವಿವಾದಗಳನ್ನು ನೀವು ಮತ್ತು ಟೆಲ್ಕೊ
          ನಡುವೆ ನೇರವಾಗಿ ನಿರ್ವಹಿಸಬೇಕು.
        </Typography>
      ),
    },
    {
      title: "26. ಮರುಪಾವತಿ ನೀತಿ",
      content: (
        <Typography variant="body2">
          ಟಿಎ ವ್ಯಾಲೆಟ್ ಪ್ಲಾಟ್ ಫಾರ್ಮ್ ನಲ್ಲಿ ಪ್ರಿಪೇಯ್ಡ್ ರೀಚಾರ್ಜ್ ನ ಎಲ್ಲಾ ಮಾರಾಟಗಳು
          ಅಂತಿಮವಾಗಿದ್ದು, ಯಾವುದೇ ಮರುಪಾವತಿ ಅಥವಾ ವಿನಿಮಯಕ್ಕೆ ಅನುಮತಿ ಇಲ್ಲ. ನಿಮ್ಮ
          ಕಾರ್ಡ್ ಅಥವಾ ಬ್ಯಾಂಕ್ ಖಾತೆಗೆ ಹಣವನ್ನು ವಿಧಿಸಲಾಗಿದ್ದು 24 ಗಂಟೆಗಳ ಒಳಗೆ
          ರೀಚಾರ್ಜ್ ವಿತರಣೆಯಾಗದಿದ್ದರೆ, ನಮ್ಮ ಗ್ರಾಹಕ ಸೇವಾ ಇ ಮೇಲ್ ವಿಳಾಸಕ್ಕೆ ಇ ಮೇಲ್
          ಕಳುಹಿಸುವ ಮೂಲಕ ನಮಗೆ ತಿಳಿಸಬೇಕು. ಇ ಮೇಲ್ ಸ್ವೀಕರಿಸಿದ ದಿನಾಂಕದಿಂದ 21 ಕೆಲಸದ
          ದಿನಗಳಲ್ಲಿ ನಿಮಗೆ ಹಣವನ್ನು ಮರುಪಾವತಿಸಲಾಗುತ್ತದೆ.
        </Typography>
      ),
    },
    {
      title: "27. ಬಿಲ್ ಪಾವತಿಗಳು",
      content: (
        <Typography variant="body2">
          ಬಿಲ್ ಪಾವತಿ ಸೇವೆ ಅಥವಾ ಯಾವುದೇ ಇತರ TA ವ್ಯಾಲೆಟ್ ಸೇವೆಯನ್ನು ಬಳಸಲು, ನೀವು
          ವರ್ಲ್ಡ್ ವೈಡ್ ವೆಬ್ ಅಥವಾ ಇಂಟರ್ನೆಟ್ ಗೆ ಪ್ರವೇಶ ಪಡೆಯಬೇಕಾಗುತ್ತದೆ. ಟಿಎ
          ವ್ಯಾಲೆಟ್ ನ ನಿಯಂತ್ರಣವನ್ನು ಮೀರಿದ ಸಂದರ್ಭಗಳಿಂದ ಪಾವತಿ ದಿನಾಂಕದಂದು ಯಾವುದೇ
          ಪಾವತಿ ಸೂಚನೆ(ಗಳ) ಮೇಲೆ ಪರಿಣಾಮ ಬೀರಲು ಸಾಧ್ಯವಾಗದಿದ್ದರೆ ಟಿಎ ವ್ಯಾಲೆಟ್
          ಯಾವುದೇ ಜವಾಬ್ದಾರಿಯನ್ನು ತೆಗೆದುಕೊಳ್ಳುವುದಿಲ್ಲ ಮತ್ತು ಯಾವುದೇ ಹೊಣೆಗಾರಿಕೆಯನ್ನು
          ಹೊಂದುವುದಿಲ್ಲ.
        </Typography>
      ),
    },
  ];
  return (
    <Box>
      <SectionTitle>ಡಿಜಿಟಲ್ ಸೇವೆಗಳು ಮತ್ತು ಬಿಲ್ ಪಾವತಿಗಳು</SectionTitle>
      {sections.map((s, i) => (
        <Accordion key={i}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "primary.dark", fontSize: "0.97rem" }}
            >
              {s.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1.5, pb: 2 }}>
            {s.content}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

function BusTicketsPanelKN() {
  return (
    <Box>
      <SectionTitle>28. ಬಸ್ ಟಿಕೆಟ್</SectionTitle>
      <Typography variant="body2" gutterBottom>
        ಟಿಎ ವ್ಯಾಲೆಟ್ ಕೇವಲ ಬಸ್ ಟಿಕೆಟ್ ಏಜೆಂಟ್ ಆಗಿದೆ. ಇದು ಬಸ್ಸುಗಳನ್ನು
        ನಿರ್ವಹಿಸುವುದಿಲ್ಲ ಅಥವಾ ಬಸ್ ಸಾರಿಗೆ ಸೇವೆಗಳನ್ನು ನೀಡುವುದಿಲ್ಲ. ಗ್ರಾಹಕರಿಗೆ ಬಸ್
        ಆಪರೇಟರ್ ಗಳು, ನಿರ್ಗಮನ ಸಮಯ ಮತ್ತು ಬೆಲೆಗಳ ಸಮಗ್ರ ಆಯ್ಕೆಯನ್ನು ಒದಗಿಸಲು, ಟಿಎ
        ವ್ಯಾಲೆಟ್ ಅನೇಕ ಬಸ್ ಆಪರೇಟರ್ ಗಳು ಮತ್ತು ಸೇವಾ ಪೂರೈಕೆದಾರರೊಂದಿಗೆ ಒಪ್ಪಂದ
        ಮಾಡಿಕೊಂಡಿದೆ.
      </Typography>
      <SubTitle>ಟಿಎ ವ್ಯಾಲೆಟ್ ನ ಜವಾಬ್ದಾರಿಗಳಲ್ಲಿ ಇವು ಸೇರಿವೆ:</SubTitle>
      <BulletList
        items={[
          "ತನ್ನ ಬಸ್ ನಿರ್ವಾಹಕರ ಜಾಲಕ್ಕೆ ಮಾನ್ಯ ಟಿಕೆಟ್ (ಬಸ್ ಆಪರೇಟರ್ ಸ್ವೀಕರಿಸುವ ಟಿಕೆಟ್) ನೀಡುವುದು;",
          "ರದ್ದತಿಯ ಸಂದರ್ಭದಲ್ಲಿ ಮರುಪಾವತಿ ಮತ್ತು ಬೆಂಬಲವನ್ನು ಒದಗಿಸುವುದು; ಮತ್ತು",
          "ಯಾವುದೇ ವಿಳಂಬ/ಅನಾನುಕೂಲತೆಯ ಸಂದರ್ಭದಲ್ಲಿ ಗ್ರಾಹಕ ಬೆಂಬಲ ಮತ್ತು ಮಾಹಿತಿಯನ್ನು ಒದಗಿಸುವುದು.",
        ]}
      />
      <SubTitle>
        TA ವ್ಯಾಲೆಟ್ ನ ಜವಾಬ್ದಾರಿಗಳು ಇವುಗಳನ್ನು ಒಳಗೊಂಡಿರುವುದಿಲ್ಲ:
      </SubTitle>
      <BulletList
        items={[
          "ಬಸ್ ನಿರ್ವಾಹಕರ ಬಸ್ ಸಮಯಕ್ಕೆ ಹೊರಡುತ್ತಿಲ್ಲ/ತಲುಪುತ್ತಿಲ್ಲ;",
          "ಬಸ್ ನಿರ್ವಾಹಕರ ಉದ್ಯೋಗಿಗಳು ವೃತ್ತಿಪರರಲ್ಲದಿರುವುದು ಅಥವಾ ಗೈರುಕಾನೂನು ನಡವಳಿಕೆಯಲ್ಲಿ ತೊಡಗಿರುವುದು;",
          "ಬಸ್ ನಿರ್ವಾಹಕರ ಬಸ್ ಸೀಟುಗಳು ಗ್ರಾಹಕರ ನಿರೀಕ್ಷೆಗೆ ಅನುಗುಣವಾಗಿಲ್ಲ;",
          "ಬಸ್ ನಿರ್ವಾಹಕರು ಯಾವುದೇ ಕಾರಣಕ್ಕಾಗಿ ಪ್ರವಾಸವನ್ನು ರದ್ದುಗೊಳಿಸುತ್ತಾರೆ;",
          "ಗ್ರಾಹಕರ ಸಾಮಾನುಗಳು ಕಳೆದುಹೋಗುತ್ತಿವೆ/ಕದಿಯಲ್ಪಟ್ಟಿವೆ/ಹಾನಿಗೊಳಗಾಗುತ್ತಿವೆ.",
        ]}
      />
      <NoteBox>
        ಟಿಕೆಟ್ ನಲ್ಲಿ ನಮೂದಿಸಿರುವ ಆಗಮನ ಮತ್ತು ನಿರ್ಗಮನ ಸಮಯಗಳು ತಾತ್ಕಾಲಿಕ ಸಮಯಗಳು
        ಮಾತ್ರ. ಆದಾಗ್ಯೂ, ಟಿಕೆಟ್ ನಲ್ಲಿ ನಮೂದಿಸಿದ ಸಮಯಕ್ಕಿಂತ ಮೊದಲು ಬಸ್ ಮೂಲ ಸ್ಥಳವನ್ನು
        ಬಿಡುವುದಿಲ್ಲ.
      </NoteBox>
      <SubTitle>ಬೋರ್ಡಿಂಗ್ ಅವಶ್ಯಕತೆಗಳು</SubTitle>
      <BulletList
        items={[
          "ಟಿಕೆಟ್ ನ ಒಂದು ಪ್ರತಿ (ಟಿಕೆಟ್ ನ ಪ್ರಿಂಟ್ ಔಟ್ ಅಥವಾ ಟಿಕೆಟ್ ಇಮೇಲ್ ನ ಪ್ರಿಂಟ್ ಔಟ್).",
          "ಗುರುತಿನ ಪುರಾವೆ (ಆಧಾರ್ ಕಾರ್ಡ್, ಡ್ರೈವಿಂಗ್ ಲೈಸೆನ್ಸ್, ವಿದ್ಯಾರ್ಥಿ ಗುರುತಿನ ಚೀಟಿ, ಕಂಪನಿ ಐಡಿ ಕಾರ್ಡ್, ಪಾಸ್ಪೋರ್ಟ್, ಪ್ಯಾನ್ ಕಾರ್ಡ್ ಅಥವಾ ಮತದಾರರ ಗುರುತಿನ ಚೀಟಿ). ಹಾಗೆ ಮಾಡಲು ವಿಫಲವಾದರೆ, ಅವರಿಗೆ ಬಸ್ ಹತ್ತಲು ಅನುಮತಿಸದೇ ಇರಬಹುದು.",
        ]}
      />
    </Box>
  );
}

function UserEnvPanelKN() {
  return (
    <Box>
      <SectionTitle>29. ಬಳಕೆದಾರ ಪರಿಸರ</SectionTitle>
      <SubTitle>ಬ್ರೌಸರ್ ಗಳು</SubTitle>
      <StyledTable
        headers={["ಬ್ರೌಸರ್", "ಕನಿಷ್ಠ ಆವೃತ್ತಿ"]}
        rows={[
          ["Chrome", "ಆವೃತ್ತಿ 64 ಮತ್ತು ಅದಕ್ಕಿಂತ ಹೆಚ್ಚಿನದು"],
          ["Safari", "ಆವೃತ್ತಿ 12.1 ಮತ್ತು ಅದಕ್ಕಿಂತ ಹೆಚ್ಚಿನದು"],
          ["Microsoft Edge", "69 ಮತ್ತು ಹೆಚ್ಚಿನದು"],
          ["Firefox", "67 ಮತ್ತು ಅದಕ್ಕಿಂತ ಹೆಚ್ಚಿನದು"],
        ]}
      />
      <SubTitle>ಸ್ಮಾರ್ಟ್ ಫೋನ್ ಪ್ಲಾಟ್ಫಾರ್ಮ್ ಗಳು</SubTitle>
      <StyledTable
        headers={["ಪ್ಲಾಟ್ಫಾರ್ಮ್", "ಕನಿಷ್ಠ ಆವೃತ್ತಿ"]}
        rows={[
          ["Android (ಸ್ಮಾರ್ಟ್ ಫೋನ್)", "ಆವೃತ್ತಿ 11.0 ಮತ್ತು ಅದಕ್ಕಿಂತ ಹೆಚ್ಚಿನದು"],
          ["iOS (iPhone)", "ಆವೃತ್ತಿ 13 ಮತ್ತು ಅದಕ್ಕಿಂತ ಹೆಚ್ಚಿನದು"],
        ]}
      />
    </Box>
  );
}

// ============================================================
// PANEL MAPS
// ============================================================
const panelsByLang = {
  en: [
    <WalletUsagePanelEN />,
    <ProductPanelEN />,
    <LoadingSpendingPanelEN />,
    <ChargesPanelEN />,
    <PrivacyPanelEN />,
    <TermsPanelEN />,
    <ServicesPanelEN />,
    <BusTicketsPanelEN />,
    <UserEnvPanelEN />,
  ],
  hi: [
    <WalletUsagePanelHI />,
    <ProductPanelHI />,
    <LoadingSpendingPanelHI />,
    <ChargesPanelHI />,
    <PrivacyPanelHI />,
    <TermsPanelHI />,
    <ServicesPanelHI />,
    <BusTicketsPanelHI />,
    <UserEnvPanelHI />,
  ],
  kn: [
    <WalletUsagePanelKN />,
    <ProductPanelKN />,
    <LoadingSpendingPanelKN />,
    <ChargesPanelKN />,
    <PrivacyPanelKN />,
    <TermsPanelKN />,
    <ServicesPanelKN />,
    <BusTicketsPanelKN />,
    <UserEnvPanelKN />,
  ],
};

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function TAWalletTerms() {
  const [tab, setTab] = useState(0);
  const [lang, setLang] = useState("en");
  const [value, setValue] = useState(0);

  const location = useLocation();

  const t = translations[lang];
  const panels = panelsByLang[lang];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");

    if (tab === "limits") {
      setValue(3);

      // 👇 WAIT for tab render
      setTimeout(() => {
        if (location.hash) {
          const element = document.getElementById(
            location.hash.replace("#", ""),
          );

          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }
      }, 500); // 👈 increase delay
    }
  }, [location]);

  useEffect(() => {
    if (value === 3 && location.hash) {
      setTimeout(() => {
        const element = document.getElementById(location.hash.replace("#", ""));

        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 300);
    }
  }, [value]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 6 }}>
        {/* Hero Header */}
        <Box
          sx={{
            background:
              "linear-gradient(135deg, #1B7A3E 0%, #145C2F 50%, #0E4422 100%)",
            pt: 5,
            pb: 4,
            px: 2,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -40,
              right: -40,
              width: 200,
              height: 200,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.04)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -30,
              left: -30,
              width: 140,
              height: 140,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.04)",
            }}
          />
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
              spacing={2}
              mb={1.5}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    bgcolor: "rgba(255,255,255,0.15)",
                    borderRadius: 2,
                    p: 1.2,
                    display: "flex",
                  }}
                >
                  <AccountBalanceWalletIcon
                    sx={{ color: "#fff", fontSize: 32 }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ color: "#fff", fontWeight: 700, letterSpacing: 0.5 }}
                  >
                    {t.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.75)",
                      fontFamily: "'Georgia', serif",
                    }}
                  >
                    {t.subtitle}
                  </Typography>
                </Box>
              </Stack>

              {/* Language Switcher */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TranslateIcon
                  sx={{ color: "rgba(255,255,255,0.8)", fontSize: 20 }}
                />
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  {["en", "hi", "kn"].map((l) => (
                    <Button
                      key={l}
                      onClick={() => {
                        setLang(l);
                        setTab(0);
                      }}
                      variant={lang === l ? "contained" : "outlined"}
                      size="small"
                      sx={{
                        minWidth: 48,
                        fontFamily: "'Georgia', serif",
                        fontWeight: lang === l ? 700 : 400,
                        fontSize: "0.78rem",
                        color:
                          lang === l ? "#1B7A3E" : "rgba(255,255,255,0.85)",
                        bgcolor: lang === l ? "#fff" : "transparent",
                        borderColor: "rgba(255,255,255,0.5)",
                        "&:hover": {
                          bgcolor:
                            lang === l ? "#fff" : "rgba(255,255,255,0.1)",
                          borderColor: "#fff",
                        },
                        textTransform: "none",
                        borderRadius: 1.5,
                        px: 1,
                      }}
                    >
                      {translations[l].langLabel}
                    </Button>
                  ))}
                </Box>
              </Box>
            </Stack>

            <Typography
              variant="h5"
              sx={{
                color: "#fff",
                fontWeight: 600,
                mb: 0.5,
                letterSpacing: 0.2,
              }}
            >
              {t.mainTitle}
            </Typography>
            <Stack
              direction="row"
              spacing={1.5}
              mt={1.5}
              flexWrap="wrap"
              gap={1}
            >
              {t.chips.map((chip, i) => (
                <Chip
                  key={i}
                  label={chip}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.18)",
                    color: "#fff",
                    fontFamily: "'Georgia', serif",
                    fontSize: "0.8rem",
                  }}
                />
              ))}
            </Stack>
          </Container>
        </Box>

        {/* Tab Navigation */}
        <Box
          sx={{
            bgcolor: "#fff",
            borderBottom: "2px solid #C8E6C9",
            position: "sticky",
            top: 0,
            zIndex: 10,
            boxShadow: "0 2px 8px rgba(27,122,62,0.07)",
          }}
        >
          <Container maxWidth="lg">
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{
                style: {
                  backgroundColor: "#1B7A3E",
                  height: 3,
                  borderRadius: 2,
                },
              }}
            >
              {t.tabs.map((label, i) => (
                <Tab key={i} label={label} />
              ))}
            </Tabs>
          </Container>
        </Box>

        {/* Content */}
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Paper
            sx={{
              p: { xs: 2.5, sm: 4 },
              borderRadius: 3,
              border: "1px solid #C8E6C9",
            }}
          >
            {panels[tab]}
          </Paper>
        </Container>

        {/* Footer */}
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Box
            sx={{
              bgcolor: "#fff",
              border: "1px solid #C8E6C9",
              borderRadius: 3,
              p: 3,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { sm: "center" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ color: "primary.dark" }}>
                {t.footer.company}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Email:{" "}
                <Link
                  href="mailto:support@taipl.in"
                  sx={{ color: "primary.main" }}
                >
                  support@taipl.in
                </Link>
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t.footer.helpDesk} <strong>9390398339 </strong>
              </Typography>
            </Box>
            <Box sx={{ textAlign: { sm: "right" } }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                <Link
                  href="https://transactionanalysts.com"
                  sx={{ color: "primary.main" }}
                >
                  transactionanalysts.com
                </Link>
              </Typography>

              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                <Link
                  href="https://www.tawallet.com"
                  sx={{ color: "primary.main" }}
                >
                  tawallet.com
                </Link>
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}